import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";

// Utilities - extracted for reuse
import { formatIST, formatISTTime, formatISTDate, getCurrentIST } from "./utils/helpers";
import { FLEETY_BLUE, CHART_COLORS, STATUS_COLORS } from "./utils/constants";

// Lucide icons
import {
  LayoutDashboard,
  UtensilsCrossed,
  Package,
  ShoppingCart,
  Store,
  Users,
  FileText,
  TrendingUp,
  LogOut,
  Menu,
  X,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Bike,
  CreditCard,
  Building,
  Bell,
  Settings,
  Power,
  Globe,
  Smartphone,
  Ticket,
  MessageSquare,
  Image,
  ExternalLink,
  UserX,
  Trash2,
  Star,
  Calendar,
  Copy,
  ClipboardList,
  UserCheck,
  User,
  Phone,
  Volume2,
  VolumeX,
  Radio,
  Filter,
  PlayCircle,
  PauseCircle,
  Edit3,
  Save,
  Landmark,
  Upload,
  Eye,
  EyeOff,
  Receipt,
  Wallet,
} from "lucide-react";

// Chart components
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API helper
const createApi = (token) => {
  const instance = axios.create({
    baseURL: API,
    timeout: 30000,
    headers: token ? { 
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    } : {},
  });
  return instance;
};

// Login Page
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("fleety_admin_token", response.data.token);
      onLogin(response.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" data-testid="login-page">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#3FA9F5] rounded-xl flex items-center justify-center mx-auto mb-4">
            <LayoutDashboard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Fleety Admin</h1>
          <p className="text-gray-500 mt-2">Sign in to your dashboard</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5] focus:border-transparent outline-none transition"
              placeholder="admin@fleety.com"
              required
              data-testid="email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5] focus:border-transparent outline-none transition"
              placeholder="••••••••"
              required
              data-testid="password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#3FA9F5] text-white py-3 rounded-lg font-medium hover:bg-[#2d8fd4] transition disabled:opacity-50"
            data-testid="login-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="text-center text-gray-400 text-sm mt-6">
          Default: admin@fleety.com / admin123
        </p>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, color = "blue", subtitle }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-testid="stat-card">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        {trend && <p className="text-sm text-green-600 mt-1">{trend}</p>}
      </div>
      <div className={`w-12 h-12 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

// Loading Spinner
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 border-4 border-[#3FA9F5] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Alert Component
const Alert = ({ type, message }) => {
  const styles = {
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
    alert: "bg-red-50 border-red-200 text-red-800",
    success: "bg-green-50 border-green-200 text-green-800",
  };
  const icons = {
    warning: AlertTriangle,
    info: Bell,
    alert: AlertCircle,
    success: CheckCircle,
  };
  const Icon = icons[type] || Bell;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${styles[type] || styles.info}`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  );
};

// Dashboard Overview with Admin Analytics
const Dashboard = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("today");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics?time_range=${timeRange}&_t=${Date.now()}`);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
    } finally {
      setLoading(false);
    }
  }, [api, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingSpinner />;

  const overview = data?.overview || {};
  const alerts = data?.alerts || [];
  const chartData = data?.chart_data || [];
  const rangeLabel = data?.range_label || "Today";

  // Get the appropriate labels for stats based on time range
  const getOrdersLabel = () => {
    if (timeRange === "today") return "Orders Today";
    if (timeRange === "week") return "Orders This Week";
    if (timeRange === "month") return "Orders This Month";
    return "Total Orders";
  };

  const getRevenueLabel = () => {
    if (timeRange === "today") return "Revenue Today";
    if (timeRange === "week") return "Revenue This Week";
    if (timeRange === "month") return "Revenue This Month";
    return "Total Revenue";
  };

  const getChartTitle = () => {
    if (timeRange === "today") return "Revenue Trend (Today - Hourly)";
    if (timeRange === "week") return "Revenue Trend (Last 7 Days)";
    if (timeRange === "month") return "Revenue Trend (Last 4 Weeks)";
    return "Revenue Trend (Last 6 Months)";
  };

  const getOrdersChartTitle = () => {
    if (timeRange === "today") return "Orders by Service (Today - Hourly)";
    if (timeRange === "week") return "Orders by Service (Last 7 Days)";
    if (timeRange === "month") return "Orders by Service (Last 4 Weeks)";
    return "Orders by Service (Last 6 Months)";
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Timeline Filter */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 mr-2">Time Period:</span>
          {[
            { value: "today", label: "Today" },
            { value: "week", label: "This Week" },
            { value: "month", label: "This Month" },
            { value: "all", label: "Overall" }
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeRange(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === option.value
                  ? "bg-[#3FA9F5] text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">Showing data for: <span className="font-medium text-gray-600">{rangeLabel}</span></p>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <Alert key={i} type={alert.type} message={alert.message} />
          ))}
        </div>
      )}

      {/* Online Status Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 shadow-sm text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Online Restaurants</p>
              <p className="text-3xl font-bold">{overview.online_restaurants || 0}</p>
              <p className="text-xs text-green-200 mt-1">of {overview.approved_restaurants || 0} approved</p>
            </div>
            <Store className="w-10 h-10 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 shadow-sm text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Online Riders</p>
              <p className="text-3xl font-bold">{overview.online_riders || 0}</p>
              <p className="text-xs text-blue-200 mt-1">of {overview.total_riders || 0} total</p>
            </div>
            <Bike className="w-10 h-10 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 shadow-sm text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Customers</p>
              <p className="text-3xl font-bold">{overview.total_customers || 0}</p>
            </div>
            <Users className="w-10 h-10 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 shadow-sm text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Active Orders</p>
              <p className="text-3xl font-bold">{overview.active_orders || 0}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title={getOrdersLabel()} 
          value={overview.period_orders || 0} 
          icon={ShoppingCart} 
          color="blue" 
        />
        <StatCard 
          title={getRevenueLabel()} 
          value={`₹${(overview.period_revenue || 0).toLocaleString()}`} 
          icon={TrendingUp} 
          color="green" 
        />
        <StatCard title="Completed" value={overview.completed_orders || 0} icon={CheckCircle} color="purple" />
        <StatCard title="Cancelled" value={overview.cancelled_orders || 0} icon={XCircle} color="red" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Open Disputes" value={overview.open_disputes || 0} icon={AlertTriangle} color="orange" />
        <StatCard title="Pending Refunds" value={overview.pending_refunds || 0} icon={CreditCard} color="pink" />
        <StatCard title="Fleety Orders" value={overview.fleety_orders || 0} icon={Store} color="indigo" />
        <StatCard title="FleetyGo Orders" value={overview.fleetygo_orders || 0} icon={Bike} color="cyan" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4">{getChartTitle()}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
              <Area type="monotone" dataKey="revenue" stroke="#3FA9F5" fill="#3FA9F5" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4">{getOrdersChartTitle()}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="fleety" fill="#3FA9F5" name="Fleety" />
              <Bar dataKey="fleetygo" fill="#10B981" name="FleetyGo" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <UtensilsCrossed className="w-6 h-6" />
            <span className="font-medium">Fleety (Food)</span>
          </div>
          <p className="text-3xl font-bold">{overview.fleety_orders || 0}</p>
          <p className="text-blue-100 text-sm">{rangeLabel} Orders</p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-6 h-6" />
            <span className="font-medium">FleetyGo (Package)</span>
          </div>
          <p className="text-3xl font-bold">{overview.fleetygo_orders || 0}</p>
          <p className="text-green-100 text-sm">{rangeLabel} Orders</p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6" />
            <span className="font-medium">Customers</span>
          </div>
          <p className="text-3xl font-bold">{overview.total_customers || 0}</p>
          <p className="text-purple-100 text-sm">Registered Users</p>
        </div>
      </div>
    </div>
  );
};

// Disputes Management
const Disputes = ({ api }) => {
  const [disputes, setDisputes] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", type: "" });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolveForm, setResolveForm] = useState({ status: "approved", refund_amount: "", notes: "" });
  const [resolving, setResolving] = useState(false);
  const [imageModal, setImageModal] = useState(null);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.type) params.append("dispute_type", filter.type);
      params.append("_t", Date.now());
      const res = await api.get(`/admin/disputes?${params.toString()}`);
      setDisputes(res.data.disputes);
      setSummary(res.data.summary);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const handleResolve = async () => {
    if (!selectedDispute) return;
    setResolving(true);
    try {
      await api.put(`/disputes/${selectedDispute.id}/resolve`, {
        status: resolveForm.status,
        refund_amount: resolveForm.refund_amount ? parseFloat(resolveForm.refund_amount) : null,
        resolution_notes: resolveForm.notes
      });
      setSelectedDispute(null);
      setResolveForm({ status: "approved", refund_amount: "", notes: "" });
      fetchDisputes();
    } catch (err) {
      console.error(err);
      alert("Failed to resolve dispute");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Disputes Management</h2>
        <button onClick={fetchDisputes} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.total || 0}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-100 text-center">
          <p className="text-2xl font-bold text-red-600">{summary.open || 0}</p>
          <p className="text-sm text-red-600">Open</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100 text-center">
          <p className="text-2xl font-bold text-green-600">{summary.approved || 0}</p>
          <p className="text-sm text-green-600">Approved</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100 text-center">
          <p className="text-2xl font-bold text-yellow-600">{summary.partial_refund || 0}</p>
          <p className="text-sm text-yellow-600">Partial</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 text-center">
          <p className="text-2xl font-bold text-blue-600">{summary.resolved || 0}</p>
          <p className="text-sm text-blue-600">Resolved</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex gap-4">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="approved">Approved</option>
          <option value="partial_refund">Partial Refund</option>
          <option value="rejected">Rejected</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          <option value="food_not_delivered">Food Not Delivered</option>
          <option value="wrong_item">Wrong Item</option>
          <option value="quality_issue">Quality Issue</option>
          <option value="rider_issue">Rider Issue</option>
        </select>
      </div>

      {/* Disputes List */}
      {loading ? (
        <LoadingSpinner />
      ) : disputes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No disputes found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((dispute) => (
            <div key={dispute.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Dispute Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-900">#{dispute.order_number}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[dispute.status] || 'bg-gray-100'}`}>
                      {dispute.status?.toUpperCase()}
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                      {dispute.dispute_type?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatIST(dispute.created_at)}
                  </div>
                </div>
              </div>

              {/* Dispute Details */}
              <div className="p-4 space-y-4">
                {/* Customer's Description */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Customer&apos;s Complaint
                  </h4>
                  <p className="text-gray-800">{dispute.description || 'No description provided'}</p>
                </div>

                {/* Order Info */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium">Order Amount</p>
                    <p className="text-xl font-bold text-blue-800">₹{dispute.order_amount?.toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium">Food Value</p>
                    <p className="text-lg font-bold text-green-800">₹{(dispute.food_value || 0)?.toLocaleString()}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-600 font-medium">Restaurant</p>
                    <p className="text-lg font-semibold text-purple-800">{dispute.restaurant_name || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 font-medium">Delivery Address</p>
                    <p className="text-sm text-gray-800 truncate">{dispute.customer_address || 'N/A'}</p>
                  </div>
                </div>

                {/* Photos/Videos Uploaded by Customer */}
                {dispute.media_urls && dispute.media_urls.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      📷 Evidence Uploaded by Customer ({dispute.media_urls.length} files)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {dispute.media_urls.map((url, index) => (
                        <div 
                          key={index} 
                          className="aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all"
                          onClick={() => setImageModal(url)}
                        >
                          <img 
                            src={url} 
                            alt={`Evidence ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolve Section */}
                {dispute.status === 'open' && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    {selectedDispute?.id === dispute.id ? (
                      <div className="bg-yellow-50 rounded-lg p-4 space-y-4">
                        <h4 className="font-semibold text-gray-900">Resolve Dispute</h4>
                        
                        {/* Financial Breakdown - Show when approving refund */}
                        {(resolveForm.status === 'approved' || resolveForm.status === 'partial_refund') && (
                          <div className="bg-white rounded-lg p-4 border border-yellow-200">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Cost Breakdown (Phase 1)</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div className="p-2 bg-red-50 rounded">
                                <p className="text-xs text-red-600">Restaurant Bears</p>
                                <p className="font-bold text-red-700">₹{(dispute.food_value || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">Food Value</p>
                              </div>
                              <div className="p-2 bg-blue-50 rounded">
                                <p className="text-xs text-blue-600">Platform Bears</p>
                                <p className="font-bold text-blue-700">₹{(dispute.platform_fee || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">Platform Fee</p>
                              </div>
                              <div className="p-2 bg-blue-50 rounded">
                                <p className="text-xs text-blue-600">Platform Bears</p>
                                <p className="font-bold text-blue-700">₹{(dispute.delivery_fee || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">Delivery Fee</p>
                              </div>
                              <div className="p-2 bg-blue-50 rounded">
                                <p className="text-xs text-blue-600">Platform Bears</p>
                                <p className="font-bold text-blue-700">₹{(dispute.gst_amount || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">GST</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              ℹ️ Restaurant will have ₹{(dispute.food_value || 0).toLocaleString()} deducted from their next payout
                            </p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                            <select
                              value={resolveForm.status}
                              onChange={(e) => setResolveForm({ ...resolveForm, status: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            >
                              <option value="approved">✅ Approve Full Refund (₹{dispute.order_amount})</option>
                              <option value="partial_refund">💰 Partial Refund</option>
                              <option value="rejected">❌ Reject Dispute</option>
                              <option value="resolved">✓ Mark as Resolved</option>
                            </select>
                          </div>
                          {resolveForm.status === 'partial_refund' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount (₹)</label>
                              <input
                                type="number"
                                placeholder={`Max: ${dispute.order_amount}`}
                                value={resolveForm.refund_amount}
                                onChange={(e) => setResolveForm({ ...resolveForm, refund_amount: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                max={dispute.order_amount}
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                            <input
                              type="text"
                              placeholder="Internal notes..."
                              value={resolveForm.notes}
                              onChange={(e) => setResolveForm({ ...resolveForm, notes: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleResolve}
                            disabled={resolving}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {resolving ? 'Processing...' : 'Submit Resolution'}
                          </button>
                          <button
                            onClick={() => setSelectedDispute(null)}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setResolveForm({ status: "approved", refund_amount: dispute.order_amount?.toString() || "", notes: "" });
                        }}
                        className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm font-medium hover:bg-[#2d8fd4]"
                      >
                        Review & Resolve Dispute
                      </button>
                    )}
                  </div>
                )}

                {/* Already Resolved Info */}
                {dispute.status !== 'open' && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-sm text-green-700">
                        <strong>Resolved:</strong> {dispute.admin_notes || 'No notes'} 
                        {dispute.refund_amount > 0 && ` | Refund: ₹${dispute.refund_amount}`}
                        {dispute.resolved_at && ` | ${formatIST(dispute.resolved_at)}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Modal */}
      {imageModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImageModal(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button 
              onClick={() => setImageModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={imageModal} 
              alt="Evidence" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Orders Management
const Orders = ({ api }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusSummary, setStatusSummary] = useState({});
  const [filters, setFilters] = useState({ service: "", status: "", search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.service) params.append("service", filters.service);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);
      params.append("_t", Date.now());
      const res = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(res.data.orders);
      setTotalPages(res.data.total_pages);
      setStatusSummary(res.data.status_summary || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, page, filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-refresh every 30 seconds for real-time status updates
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput });
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Orders Management</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#3FA9F5] focus:ring-[#3FA9F5]"
            />
            Auto-refresh (30s)
          </label>
          <button onClick={fetchOrders} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(statusSummary).map(([status, count]) => (
          <button
            key={status}
            onClick={() => { setFilters({ ...filters, status: filters.status === status ? "" : status }); setPage(1); }}
            className={`px-3 py-1 rounded-full text-sm font-medium transition ${filters.status === status ? 'bg-[#3FA9F5] text-white' : STATUS_COLORS[status] || 'bg-gray-100'}`}
          >
            {status}: {count}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by order number, restaurant..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
              Search
            </button>
          </form>
          <div className="flex gap-4">
            <select
              value={filters.service}
              onChange={(e) => { setFilters({ ...filters, service: e.target.value }); setPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Services</option>
              <option value="fleety">Fleety (Food)</option>
              <option value="fleetygo">FleetyGo (Package)</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="placed">Placed</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="ready">Ready</option>
              <option value="picked_up">Picked Up</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="auto_cancelled">Auto-Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Order #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Service</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Details</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Total</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    >
                      <td className="px-4 py-3 font-medium">#{order.order_number}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${order.service === "fleety" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
                          {order.service === "fleety" ? "Food" : "Package"}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[200px] truncate">
                        {order.service === "fleety" ? order.restaurant_name : order.pickup_address}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${STATUS_COLORS[order.status] || "bg-gray-100"}`}>
                            {order.status?.replace("_", " ")}
                          </span>
                          {order.cancellation_reason && (
                            <span className="text-xs text-red-600 flex items-center gap-1" title={order.cancellation_reason}>
                              <AlertCircle className="w-3 h-3" />
                              {order.cancellation_reason.length > 30 ? order.cancellation_reason.substring(0, 30) + '...' : order.cancellation_reason}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium">₹{order.total?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-500">{formatIST(order.created_at)}</td>
                      <td className="px-4 py-3">
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} />
                      </td>
                    </tr>
                    {/* Expanded Order Details with GST Breakdown */}
                    {expandedOrder === order.id && order.service === "fleety" && (() => {
                      // Calculate actual item total from items array
                      const itemsTotal = order.items?.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0) || 0;
                      const packingCharges = order.packing_charges || 0;
                      const foodSubtotal = itemsTotal + packingCharges; // Food + Packing
                      const deliveryFee = order.delivery_fee || 0;
                      const platformFee = order.platform_fee || 0;
                      
                      // Calculate GST: Food+Packing @ 5%, Delivery+Platform @ 18%
                      const foodGst = order.food_gst || order.food_gst_amount || (foodSubtotal * 0.05);
                      const serviceGst = order.service_gst || order.service_gst_amount || ((deliveryFee + platformFee) * 0.18);
                      const totalGst = foodGst + serviceGst;
                      
                      // Settlement calculations
                      const restaurantSettlement = foodSubtotal + foodGst; // Item + Packing + Food GST
                      const riderSettlement = deliveryFee;
                      const fleetySettlement = platformFee + serviceGst; // Platform + Service GST
                      
                      return (
                      <tr>
                        <td colSpan="7" className="px-4 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Order Summary */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <Receipt className="w-4 h-4" /> Order Summary
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Item Total:</span>
                                  <span>₹{itemsTotal.toFixed(2)}</span>
                                </div>
                                {packingCharges > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Packing Charges:</span>
                                    <span>₹{packingCharges.toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-gray-500 border-t pt-1">
                                  <span>Subtotal (Food + Packing):</span>
                                  <span>₹{foodSubtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-orange-600">
                                  <span>GST on Food & Packing (5%):</span>
                                  <span>₹{foodGst.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between mt-2">
                                  <span className="text-gray-600">Delivery Fee:</span>
                                  <span>₹{deliveryFee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Platform Fee:</span>
                                  <span>₹{platformFee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-blue-600">
                                  <span>GST on Delivery & Platform (18%):</span>
                                  <span>₹{serviceGst.toFixed(2)}</span>
                                </div>
                                <div className="border-t pt-2 flex justify-between font-semibold">
                                  <span>Total Paid:</span>
                                  <span className="text-[#3FA9F5]">₹{(order.total || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>

                            {/* GST Breakdown */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FileText className="w-4 h-4" /> GST Breakdown
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Food GST (5%):</span>
                                  <span className="text-orange-600">₹{foodGst.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 pl-2">On ₹{foodSubtotal.toFixed(2)} (Item + Packing)</div>
                                <div className="text-xs text-gray-500 pl-2">→ Restaurant remits to govt</div>
                                <div className="flex justify-between mt-2">
                                  <span className="text-gray-600">Service GST (18%):</span>
                                  <span className="text-blue-600">₹{serviceGst.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 pl-2">On ₹{(deliveryFee + platformFee).toFixed(2)} (Delivery + Platform)</div>
                                <div className="text-xs text-gray-500 pl-2">→ Fleety remits to govt</div>
                                <div className="border-t pt-2 flex justify-between font-semibold">
                                  <span>Total GST:</span>
                                  <span>₹{totalGst.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Settlement Breakdown */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <Wallet className="w-4 h-4" /> Settlement
                              </h4>
                              <div className="space-y-2 text-sm">
                                {/* Restaurant Settlement */}
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Restaurant:</span>
                                  <span className="text-green-600 font-medium">
                                    ₹{restaurantSettlement.toFixed(2)}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 pl-2">
                                  Item ₹{itemsTotal.toFixed(2)} + Packing ₹{packingCharges.toFixed(2)} + GST ₹{foodGst.toFixed(2)}
                                </div>
                                <div className="text-xs text-orange-500 pl-2">(incl. ₹{foodGst.toFixed(2)} GST → Govt)</div>
                                
                                {/* Rider Settlement */}
                                <div className="flex justify-between mt-3">
                                  <span className="text-gray-600">Rider:</span>
                                  <span className="text-purple-600 font-medium">₹{riderSettlement.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 pl-2">Delivery Fee</div>
                                
                                {/* Fleety Settlement */}
                                <div className="flex justify-between mt-3">
                                  <span className="text-gray-600">Fleety:</span>
                                  <span className="text-blue-600 font-medium">₹{fleetySettlement.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 pl-2">
                                  Platform ₹{platformFee.toFixed(2)} + GST ₹{serviceGst.toFixed(2)}
                                </div>
                                <div className="text-xs text-orange-500 pl-2">(incl. ₹{serviceGst.toFixed(2)} GST → Govt)</div>
                                
                                {/* Total to Government */}
                                <div className="border-t mt-3 pt-2 flex justify-between text-orange-600">
                                  <span className="font-medium">Total to Govt:</span>
                                  <span className="font-medium">₹{totalGst.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Payment:</span>
                              <span className="ml-2 font-medium">{order.payment_method || 'N/A'}</span>
                              {order.payment_status && (
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {order.payment_status}
                                </span>
                              )}
                            </div>
                            <div>
                              <span className="text-gray-500">Rider:</span>
                              <span className="ml-2 font-medium">{order.rider_name || 'Not assigned'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Delivery:</span>
                              <span className="ml-2">{order.customer_address || 'N/A'}</span>
                            </div>
                            {order.otp && (
                              <div>
                                <span className="text-gray-500">OTP:</span>
                                <span className="ml-2 font-mono font-medium">{order.otp}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})()}
                    {/* Expanded FleetyGo Order */}
                    {expandedOrder === order.id && order.service === "fleetygo" && (
                      <tr>
                        <td colSpan="7" className="px-4 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Pickup:</span>
                              <span className="ml-2">{order.pickup_address || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Drop:</span>
                              <span className="ml-2">{order.drop_address || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Package:</span>
                              <span className="ml-2">{order.package_type || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Distance:</span>
                              <span className="ml-2">{order.distance_km || 0} km</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Refunds Management
const Refunds = ({ api }) => {
  const [refunds, setRefunds] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false); // Toggle to show non-refundable orders
  const [retrying, setRetrying] = useState(null);
  const [processedRefunds, setProcessedRefunds] = useState({}); // Track processed refunds by order_id

  const fetchRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append("status", filter);
      if (priorityOnly) params.append("priority_only", "true");
      // By default, only show refundable orders unless showAllOrders is true
      params.append("refundable_only", showAllOrders ? "false" : "true");
      params.append("_t", Date.now());
      const res = await api.get(`/admin/refunds?${params.toString()}`);
      setRefunds(res.data.refunds);
      setSummary(res.data.summary);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, priorityOnly, showAllOrders]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  const [refundResult, setRefundResult] = useState(null);

  const handleRetry = async (orderId) => {
    setRetrying(orderId);
    setRefundResult(null);
    try {
      const res = await api.post(`/admin/refunds/${orderId}/process`);
      const result = {
        success: res.data.success,
        message: res.data.message,
        status: res.data.status,
        amount: res.data.amount
      };
      
      // Track the processed refund status
      setProcessedRefunds(prev => ({
        ...prev,
        [orderId]: result
      }));
      
      setRefundResult({
        orderId,
        ...result
      });
      
      if (res.data.success) {
        // Refresh after 5 seconds to show updated status
        setTimeout(() => {
          fetchRefunds();
        }, 5000);
      }
    } catch (err) {
      const errorResult = {
        success: false,
        message: err.response?.data?.detail || 'Failed to process refund',
        status: 'error'
      };
      setProcessedRefunds(prev => ({
        ...prev,
        [orderId]: errorResult
      }));
      setRefundResult({
        orderId,
        ...errorResult
      });
    } finally {
      setRetrying(null);
    }
  };

  // Helper to get button state for a refund
  const getRefundButtonState = (refund) => {
    const processed = processedRefunds[refund.order_id];
    if (retrying === refund.order_id) {
      return { text: '⏳ Processing...', disabled: true, className: 'bg-gray-400 cursor-not-allowed' };
    }
    if (processed) {
      if (processed.status === 'INITIATED' || processed.status === 'PENDING') {
        return { text: '✓ Initiated', disabled: true, className: 'bg-green-500 cursor-default' };
      }
      if (processed.status === 'SUCCESS' || processed.status === 'completed') {
        return { text: '✓ Completed', disabled: true, className: 'bg-green-600 cursor-default' };
      }
      if (processed.status === 'not_paid') {
        return { text: '✗ Not Paid', disabled: true, className: 'bg-gray-400 cursor-default' };
      }
      if (processed.status === 'already_refunded') {
        return { text: '✓ Already Refunded', disabled: true, className: 'bg-green-600 cursor-default' };
      }
      if (processed.status === 'no_payment' || processed.status === 'error') {
        return { text: '✗ No Payment', disabled: true, className: 'bg-gray-400 cursor-default' };
      }
      if (processed.status === 'failed') {
        return { text: '↻ Retry', disabled: false, className: 'bg-red-500 hover:bg-red-600' };
      }
    }
    
    // Check if the order is not refundable based on backend data
    if (!refund.is_refundable) {
      if (refund.non_refundable_reason === 'already_refunded') {
        return { text: '✓ Already Refunded', disabled: true, className: 'bg-green-600 cursor-default' };
      }
      if (refund.non_refundable_reason === 'not_paid') {
        return { text: '✗ Not Paid', disabled: true, className: 'bg-gray-400 cursor-default' };
      }
      if (refund.non_refundable_reason === 'no_payment_ref') {
        return { text: '✗ No Payment Ref', disabled: true, className: 'bg-gray-400 cursor-default' };
      }
      return { text: '✗ Not Refundable', disabled: true, className: 'bg-gray-400 cursor-default' };
    }
    
    // Default pending state - refundable order
    return { 
      text: 'Process Refund', 
      disabled: false, 
      className: refund.is_priority ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600' 
    };
  };

  return (
    <div className="space-y-6" data-testid="refunds-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Refunds Management</h2>
        <button onClick={fetchRefunds} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Refund Result Alert */}
      {refundResult && (
        <div className={`p-4 rounded-lg border ${refundResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-3">
            {refundResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <div>
              <p className={`font-medium ${refundResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {refundResult.message}
              </p>
              {refundResult.success && refundResult.amount && (
                <p className="text-sm text-green-600">
                  Refund amount: ₹{refundResult.amount.toLocaleString()} • Status: {refundResult.status}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total Refunds</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total || 0}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200 cursor-pointer hover:bg-orange-100 transition" onClick={() => setPriorityOnly(!priorityOnly)}>
          <div className="flex items-center gap-2">
            <p className="text-sm text-orange-600">🚨 Priority</p>
            {priorityOnly && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded">FILTERED</span>}
          </div>
          <p className="text-2xl font-bold text-orange-600">{summary.priority || 0}</p>
          <p className="text-xs text-orange-500">Auto-cancelled</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
          <p className="text-sm text-yellow-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{summary.pending || 0}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-sm text-green-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">{summary.completed || 0}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-100">
          <p className="text-sm text-red-600">Failed</p>
          <p className="text-2xl font-bold text-red-600">{summary.failed || 0}</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
        <p className="text-purple-100 text-sm">Total Refund Amount</p>
        <p className="text-3xl font-bold">₹{(summary.total_amount || 0).toLocaleString()}</p>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {['', 'pending', 'completed', 'failed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === status ? 'bg-[#3FA9F5] text-white' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
            >
              {status || 'Actionable'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer bg-white px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showAllOrders}
              onChange={(e) => setShowAllOrders(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#3FA9F5] focus:ring-[#3FA9F5]"
              data-testid="show-all-orders-toggle"
            />
            Show all orders (including non-refundable)
          </label>
        </div>
      </div>

      {/* Info Banner */}
      {!showAllOrders && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Showing only actionable orders (paid + has Cashfree reference + not yet refunded). Toggle "Show all orders" to see everything.</span>
        </div>
      )}

      {/* Refunds List */}
      {loading ? (
        <LoadingSpinner />
      ) : refunds.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">{showAllOrders ? 'No refunds found' : 'No actionable refunds - all orders are either already refunded, not paid, or missing payment reference'}</p>
          {!showAllOrders && (
            <button 
              onClick={() => setShowAllOrders(true)}
              className="mt-3 text-[#3FA9F5] text-sm hover:underline"
            >
              Show all orders →
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Order #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Service</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Payment</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Reason</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {refunds.map((refund) => (
                  <tr key={refund.id} className={`hover:bg-gray-50 ${refund.is_priority ? 'bg-orange-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">#{refund.order_number}</span>
                        {refund.is_priority && (
                          <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded animate-pulse">
                            🚨 PRIORITY
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${refund.service === "fleety" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
                        {refund.service === "fleety" ? "Food" : "Package"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">₹{refund.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-500">{refund.payment_method}</td>
                    <td className="px-4 py-3">
                      <div className="max-w-[200px]">
                        {refund.is_auto_cancelled ? (
                          <span className="text-xs text-orange-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {refund.cancellation_reason || 'Auto-cancelled (No rider)'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">{refund.reason || 'Order cancelled'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[refund.status] || "bg-gray-100"}`}>
                        {refund.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const btnState = getRefundButtonState(refund);
                        return (
                          <button
                            onClick={() => !btnState.disabled && handleRetry(refund.order_id)}
                            disabled={btnState.disabled}
                            className={`px-3 py-1.5 text-white rounded text-xs font-medium transition-all ${btnState.className} disabled:opacity-80`}
                          >
                            {btnState.text}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Settlements
const Settlements = ({ api }) => {
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [orderNumberFilter, setOrderNumberFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchSettlements = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/admin/settlements?_t=${Date.now()}`;
      if (selectedRestaurant) url += `&restaurant_id=${selectedRestaurant}`;
      if (orderNumberFilter) url += `&order_number=${orderNumberFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await api.get(url);
      setSettlements(res.data.settlements);
      setSummary(res.data.summary);
      setRestaurants(res.data.restaurants || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant, orderNumberFilter, statusFilter]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  const clearFilters = () => {
    setSelectedRestaurant("");
    setOrderNumberFilter("");
    setStatusFilter("");
  };

  return (
    <div className="space-y-6" data-testid="settlements-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Restaurant Settlements</h2>
        <button onClick={fetchSettlements} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Restaurant</label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-restaurant"
            >
              <option value="">All Restaurants</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Order Number</label>
            <input
              type="text"
              value={orderNumberFilter}
              onChange={(e) => setOrderNumberFilter(e.target.value)}
              placeholder="Search order #"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-order-number"
            />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="deducted">Deducted</option>
            </select>
          </div>
          <button 
            onClick={clearFilters} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Gross (Food)</p>
          <p className="text-2xl font-bold text-gray-900">₹{(summary.total_gross || 0).toLocaleString()}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <p className="text-sm text-blue-600">Platform Fees</p>
          <p className="text-2xl font-bold text-blue-600">₹{(summary.total_platform_fees || 0).toLocaleString()}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
          <p className="text-sm text-purple-600">Delivery (Rider)</p>
          <p className="text-2xl font-bold text-purple-600">₹{(summary.total_delivery_fees || 0).toLocaleString()}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
          <p className="text-sm text-yellow-600">GST</p>
          <p className="text-2xl font-bold text-yellow-600">₹{(summary.total_gst || 0).toLocaleString()}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-100">
          <p className="text-sm text-red-600">Deductions</p>
          <p className="text-2xl font-bold text-red-600">-₹{(summary.total_deductions || 0).toLocaleString()}</p>
          <p className="text-xs text-red-500">{summary.orders_with_deductions || 0} orders</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-sm text-green-600">Net Payable</p>
          <p className="text-2xl font-bold text-green-600">₹{(summary.total_adjusted_payable || summary.total_net_payable || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Settlements Table - Individual Orders */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm text-gray-600">{summary.total_orders || 0} orders found</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Order #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date & Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Restaurant</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rider</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Gross</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Platform Fee</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Delivery Fee</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">GST</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Deduction</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Net Payable</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {settlements.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                      No settlements found
                    </td>
                  </tr>
                ) : (
                  settlements.map((s, i) => (
                    <tr key={s.order_id || i} className={`hover:bg-gray-50 ${s.has_deduction ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded font-medium">
                          {s.order_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {s.order_date ? new Date(s.order_date).toLocaleDateString('en-IN', { 
                          day: '2-digit', month: 'short', year: 'numeric'
                        }) + ', ' + new Date(s.order_date).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit', hour12: true
                        }) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium">{s.restaurant_name}</span>
                          {s.restaurant_fleety_id && (
                            <span className="ml-1 text-xs text-gray-400 font-mono">({s.restaurant_fleety_id})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium">{s.rider_name || 'Unassigned'}</span>
                          {s.rider_fleety_id && (
                            <span className="ml-1 text-xs text-gray-400 font-mono">({s.rider_fleety_id})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">₹{s.gross_amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-blue-600">₹{s.platform_fee?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-purple-600">₹{s.delivery_fee?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-yellow-600">₹{s.gst?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        {s.has_deduction ? (
                          <div className="text-red-600">
                            <span className="font-medium">-₹{s.deduction_amount?.toLocaleString()}</span>
                            <p className="text-xs text-red-500 truncate max-w-[120px]" title={s.deduction_reason}>
                              {s.deduction_reason}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${s.adjusted_net_payable < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {s.adjusted_net_payable < 0 ? '-' : ''}₹{Math.abs(s.adjusted_net_payable || s.net_payable)?.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          s.status === 'paid' ? 'bg-green-100 text-green-800' : 
                          s.status === 'deducted' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Rider Penalties
const RiderPenalties = ({ api }) => {
  const [riders, setRiders] = useState([]);
  const [totalPenalties, setTotalPenalties] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/rider-penalties?_t=${Date.now()}`);
      setRiders(res.data.riders);
      setTotalPenalties(res.data.total_penalties);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="rider-penalties-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Rider Penalties</h2>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Bike className="w-6 h-6" />
          <span className="font-medium">Total Penalties Collected</span>
        </div>
        <p className="text-3xl font-bold">₹{totalPenalties?.toLocaleString()}</p>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : riders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Bike className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No rider penalties found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rider</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Cancelled Orders</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Total Penalty</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Last Incident</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {riders.map((rider, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{rider.rider_name}</td>
                    <td className="px-4 py-3 text-gray-500">{rider.rider_phone || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                        {rider.cancelled_orders}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-red-600">₹{rider.total_penalty?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {rider.last_incident ? formatIST(rider.last_incident) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Restaurant Deductions Page
const Deductions = ({ api }) => {
  const [deductions, setDeductions] = useState([]);
  const [summary, setSummary] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [payoutWeeks, setPayoutWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [weekFilter, setWeekFilter] = useState("");

  const fetchDeductions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/admin/deductions?_t=${Date.now()}`;
      if (selectedRestaurant) url += `&restaurant_id=${selectedRestaurant}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (statusFilter) url += `&payout_status=${statusFilter}`;
      if (weekFilter) url += `&payout_week=${weekFilter}`;
      
      const res = await api.get(url);
      setDeductions(res.data.deductions || []);
      setSummary(res.data.summary || {});
      setRestaurants(res.data.restaurants || []);
      setPayoutWeeks(res.data.payout_weeks || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant, typeFilter, statusFilter, weekFilter]);

  useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]);

  const clearFilters = () => {
    setSelectedRestaurant("");
    setTypeFilter("");
    setStatusFilter("");
    setWeekFilter("");
  };

  const handleStatusChange = async (deductionId, newStatus) => {
    try {
      await api.put(`/admin/deductions/${deductionId}`, { payout_status: newStatus });
      fetchDeductions();
    } catch (err) {
      console.error(err);
      alert("Failed to update deduction status");
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'refund': return 'bg-red-100 text-red-800';
      case 'penalty': return 'bg-orange-100 text-orange-800';
      case 'adjustment': return 'bg-blue-100 text-blue-800';
      case 'manual': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'applied': return 'bg-green-100 text-green-800';
      case 'waived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="deductions-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Restaurant Deductions</h2>
          <p className="text-sm text-gray-500">Track refund deductions, penalties, and adjustments</p>
        </div>
        <button onClick={fetchDeductions} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total Deductions</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total_deductions || 0}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-100">
          <p className="text-sm text-red-600">Total Amount</p>
          <p className="text-2xl font-bold text-red-600">₹{(summary.total_amount || 0).toLocaleString()}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
          <p className="text-sm text-yellow-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">₹{(summary.pending_amount || 0).toLocaleString()}</p>
          <p className="text-xs text-yellow-500">{summary.pending_count || 0} items</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-sm text-green-600">Applied</p>
          <p className="text-2xl font-bold text-green-600">₹{(summary.applied_amount || 0).toLocaleString()}</p>
          <p className="text-xs text-green-500">{summary.applied_count || 0} items</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-600">Waived</p>
          <p className="text-2xl font-bold text-gray-600">₹{(summary.waived_amount || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">{summary.waived_count || 0} items</p>
        </div>
      </div>

      {/* By Type Breakdown */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Breakdown by Type</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-600 font-medium">Refunds</p>
            <p className="text-lg font-bold text-red-700">₹{(summary.by_type?.refund || 0).toLocaleString()}</p>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <p className="text-xs text-orange-600 font-medium">Penalties</p>
            <p className="text-lg font-bold text-orange-700">₹{(summary.by_type?.penalty || 0).toLocaleString()}</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600 font-medium">Adjustments</p>
            <p className="text-lg font-bold text-blue-700">₹{(summary.by_type?.adjustment || 0).toLocaleString()}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-600 font-medium">Manual</p>
            <p className="text-lg font-bold text-purple-700">₹{(summary.by_type?.manual || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Restaurant</label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Restaurants</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Types</option>
              <option value="refund">Refund</option>
              <option value="penalty">Penalty</option>
              <option value="adjustment">Adjustment</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="applied">Applied</option>
              <option value="waived">Waived</option>
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Payout Week</label>
            <select
              value={weekFilter}
              onChange={(e) => setWeekFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Weeks</option>
              {payoutWeeks.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
          <button 
            onClick={clearFilters} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Deductions Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm text-gray-600">{deductions.length} deductions found</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Order #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Restaurant</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Reason</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Week</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deductions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No deductions found
                    </td>
                  </tr>
                ) : (
                  deductions.map((d, i) => (
                    <tr key={d.id || i} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {d.order_number ? (
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded font-medium">
                            {d.order_number}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{d.restaurant_name || 'Unknown'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(d.type)}`}>
                          {d.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">
                        -₹{(d.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 truncate block max-w-[200px]" title={d.reason}>
                          {d.reason || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{d.payout_week || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(d.payout_status)}`}>
                          {d.payout_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {d.payout_status === 'pending' && (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => handleStatusChange(d.id, 'applied')}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                              title="Mark as Applied"
                            >
                              Apply
                            </button>
                            <button
                              onClick={() => handleStatusChange(d.id, 'waived')}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              title="Waive this deduction"
                            >
                              Waive
                            </button>
                          </div>
                        )}
                        {d.payout_status !== 'pending' && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Customers Page
const Customers = ({ api }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [summary, setSummary] = useState({ total: 0, active: 0, suspended: 0 });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(null);
  const [adminComments, setAdminComments] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("_t", Date.now());
      const res = await api.get(`/admin/cust?${params.toString()}`);
      setCustomers(res.data.customers);
      setTotalPages(res.data.total_pages);
      setSummary(res.data.summary || { total: res.data.total, active: 0, suspended: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, page, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (customerId, newStatus, customerName, comments = null) => {
    const actionText = newStatus === 'suspended' ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${actionText} "${customerName}"?`)) return;
    
    setProcessing(customerId);
    try {
      await api.put(`/admin/customers/${customerId}/status`, { 
        status: newStatus,
        admin_comments: comments
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${actionText} customer`);
    } finally {
      setProcessing(null);
    }
  };

  const openCommentsModal = (customer, action) => {
    setShowCommentsModal({ customer, action });
    setAdminComments(customer.admin_comments || "");
  };

  const handleCommentsSubmit = async () => {
    if (!showCommentsModal) return;
    const { customer, action } = showCommentsModal;
    await handleStatusChange(customer.id, action, customer.full_name || 'Customer', adminComments);
    setShowCommentsModal(null);
    setAdminComments("");
  };

  // Fetch detailed customer info when viewing
  const fetchCustomerDetails = useCallback(async (customerId) => {
    setLoadingDetails(true);
    try {
      const res = await api.get(`/admin/customers/${customerId}?_t=${Date.now()}`);
      setCustomerDetails(res.data);
    } catch (err) {
      console.error('Error fetching customer details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [api]);

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetails(customer.id);
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setCustomerDetails(null);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Customers ({summary.total})</h2>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div 
          onClick={() => { setStatusFilter('all'); setPage(1); }}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'all' ? 'border-[#3FA9F5] ring-2 ring-[#3FA9F5]/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Total Customers</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total || 0}</p>
        </div>
        <div 
          onClick={() => { setStatusFilter('active'); setPage(1); }}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{summary.active || 0}</p>
        </div>
        <div 
          onClick={() => { setStatusFilter('suspended'); setPage(1); }}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'suspended' ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Suspended</p>
          <p className="text-2xl font-bold text-orange-600">{summary.suspended || 0}</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, phone, email, Fleety ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : customers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No customers found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {customers.map((c) => {
            const status = c.status || 'active';
            const isActive = status === 'active';
            const isSuspended = status === 'suspended';
            
            return (
              <div key={c.id} className={`bg-white rounded-xl p-4 shadow-sm border ${isSuspended ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'}`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {(c.full_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{c.full_name || 'Unknown'}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${isActive ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {isActive ? '✓ Active' : '⏸ Suspended'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{c.phone_number} {c.email && `• ${c.email}`}</p>
                    {c.fleety_id && <p className="text-xs text-gray-400 font-mono">ID: {c.fleety_id}</p>}
                    {c.admin_comments && (
                      <p className="text-xs text-orange-600 mt-1 truncate">💬 {c.admin_comments}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-6 text-center">
                    <div>
                      <p className="text-xl font-bold text-gray-900">{c.total_orders || 0}</p>
                      <p className="text-xs text-gray-500">Orders</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-blue-600">{c.fleety_orders || 0}</p>
                      <p className="text-xs text-gray-500">Fleety</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-600">{c.fleetygo_orders || 0}</p>
                      <p className="text-xs text-gray-500">FleetyGo</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#3FA9F5]">₹{(c.total_spent || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Spent</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleViewCustomer(c)}
                      className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]"
                      data-testid={`view-customer-${c.id}`}
                    >
                      View
                    </button>
                    {isActive ? (
                      <button
                        onClick={() => openCommentsModal(c, 'suspended')}
                        disabled={processing === c.id}
                        className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm hover:bg-orange-200 disabled:opacity-50"
                        data-testid={`suspend-customer-${c.id}`}
                      >
                        {processing === c.id ? '...' : 'Suspend'}
                      </button>
                    ) : (
                      <button
                        onClick={() => openCommentsModal(c, 'active')}
                        disabled={processing === c.id}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 disabled:opacity-50"
                        data-testid={`reactivate-customer-${c.id}`}
                      >
                        {processing === c.id ? '...' : 'Reactivate'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-100">
          <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border disabled:opacity-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border disabled:opacity-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Customer Details Modal - Enhanced */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div 
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="customer-details-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                  {(selectedCustomer.full_name || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedCustomer.full_name || 'Unknown Customer'}</h2>
                  <p className="text-gray-500 text-sm">{selectedCustomer.phone_number}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                    (customerDetails?.status || selectedCustomer.status) === 'suspended' 
                      ? 'bg-orange-100 text-orange-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {(customerDetails?.status || selectedCustomer.status) === 'suspended' ? '⏸ Suspended' : '✓ Active'}
                  </span>
                </div>
              </div>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Basic Info Section */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-4 h-4" /> Basic Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">Full Name</label>
                        <p className="font-medium">{customerDetails?.full_name || selectedCustomer.full_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Phone Number</label>
                        <p className="font-medium">{customerDetails?.phone_number || selectedCustomer.phone_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p className="font-medium">{customerDetails?.email || selectedCustomer.email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Fleety ID</label>
                        <p className="font-medium font-mono">{customerDetails?.fleety_id || selectedCustomer.fleety_id || 'Not assigned'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Member Since</label>
                        <p className="font-medium">
                          {customerDetails?.created_at 
                            ? new Date(customerDetails.created_at).toLocaleDateString('en-IN', { 
                                year: 'numeric', month: 'short', day: 'numeric' 
                              })
                            : 'N/A'
                          }
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Total Spent</label>
                        <p className="font-medium text-green-600">₹{(customerDetails?.total_spent || selectedCustomer.total_spent || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Statistics Section */}
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" /> Order Statistics
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{customerDetails?.fleety_orders || selectedCustomer.fleety_orders || 0}</p>
                        <p className="text-xs text-gray-500">Fleety Orders</p>
                        <p className="text-xs text-blue-400">Food Delivery</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{customerDetails?.fleetygo_orders || selectedCustomer.fleetygo_orders || 0}</p>
                        <p className="text-xs text-gray-500">FleetyGo Orders</p>
                        <p className="text-xs text-green-400">Package Delivery</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{customerDetails?.total_orders || selectedCustomer.total_orders || 0}</p>
                        <p className="text-xs text-gray-500">Total Orders</p>
                        <p className="text-xs text-purple-400">All Time</p>
                      </div>
                    </div>
                  </div>

                  {/* Recent Orders Section */}
                  {customerDetails?.recent_orders && customerDetails.recent_orders.length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" /> Recent Orders
                      </h3>
                      <div className="space-y-2">
                        {customerDetails.recent_orders.slice(0, 5).map((order, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">
                                #{order.order_number || order.id?.substring(0, 8).toUpperCase()}
                              </p>
                              <p className="text-xs text-gray-500">
                                {order.restaurant_name || (order.sender_address ? 'FleetyGo Delivery' : 'Unknown')}
                              </p>
                              <p className="text-xs text-gray-400">
                                {order.created_at && new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">₹{(order.total || 0).toFixed(2)}</p>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                order.status === 'cancelled' || order.status === 'auto_cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Account Status Section */}
                  <div className={`p-4 rounded-xl ${
                    (customerDetails?.status || selectedCustomer.status) === 'suspended' 
                      ? 'bg-orange-50 border border-orange-200' 
                      : 'bg-gray-50'
                  }`}>
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Account Status
                    </h3>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Current Status</p>
                        <p className={`font-semibold ${
                          (customerDetails?.status || selectedCustomer.status) === 'suspended' 
                            ? 'text-orange-600' 
                            : 'text-green-600'
                        }`}>
                          {(customerDetails?.status || selectedCustomer.status) === 'suspended' ? '⏸ Suspended' : '✓ Active'}
                        </p>
                        {customerDetails?.status_updated_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            Updated: {new Date(customerDetails.status_updated_at).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                      {(customerDetails?.status || selectedCustomer.status) === 'suspended' ? (
                        <button
                          onClick={() => openCommentsModal(selectedCustomer, 'active')}
                          disabled={processing === selectedCustomer.id}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:opacity-50"
                          data-testid="reactivate-customer-modal-btn"
                        >
                          {processing === selectedCustomer.id ? '...' : 'Reactivate'}
                        </button>
                      ) : (
                        <button
                          onClick={() => openCommentsModal(selectedCustomer, 'suspended')}
                          disabled={processing === selectedCustomer.id}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50"
                          data-testid="suspend-customer-modal-btn"
                        >
                          {processing === selectedCustomer.id ? '...' : 'Suspend'}
                        </button>
                      )}
                    </div>
                    {(customerDetails?.admin_comments || selectedCustomer.admin_comments) && (
                      <div className="bg-white/50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Admin Comments</p>
                        <p className="text-sm text-gray-700">{customerDetails?.admin_comments || selectedCustomer.admin_comments}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal for Suspend/Reactivate */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCommentsModal(null)}>
          <div 
            className="bg-white rounded-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {showCommentsModal.action === 'suspended' ? '⏸ Suspend Customer' : '✓ Reactivate Customer'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {showCommentsModal.action === 'suspended' 
                ? `Are you sure you want to suspend "${showCommentsModal.customer.full_name || 'this customer'}"?`
                : `Are you sure you want to reactivate "${showCommentsModal.customer.full_name || 'this customer'}"?`
              }
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Comments (optional)</label>
              <textarea
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                placeholder="Add a note about this action..."
                className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCommentsModal(null)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCommentsSubmit}
                disabled={processing}
                className={`flex-1 px-4 py-2 rounded-lg text-sm text-white ${
                  showCommentsModal.action === 'suspended' 
                    ? 'bg-orange-500 hover:bg-orange-600' 
                    : 'bg-green-500 hover:bg-green-600'
                } disabled:opacity-50`}
              >
                {processing ? '...' : (showCommentsModal.action === 'suspended' ? 'Suspend' : 'Reactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Registration Requests Page
const RegistrationRequests = ({ api }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tableMissing, setTableMissing] = useState(false);
  const [setupSql, setSetupSql] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('status', filter);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 100); // Fetch more to calculate stats
      
      const res = await api.get(`/admin/registration-requests?${params.toString()}`);
      const requestsData = res.data.requests || [];
      setRequests(requestsData);
      setTotalPages(res.data.total_pages || 1);
      
      // Calculate stats from data when no filter applied
      if (!filter && !search) {
        const calculatedStats = {
          total: requestsData.length,
          pending: requestsData.filter(r => r.status === 'pending').length,
          approved: requestsData.filter(r => r.status === 'approved').length,
          rejected: requestsData.filter(r => r.status === 'rejected').length
        };
        setStats(calculatedStats);
      }
      
      if (res.data.table_missing) {
        setTableMissing(true);
        setSetupSql(res.data.setup_sql || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, search, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/registration-requests/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests, fetchStats]);

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this registration? This will create a new restaurant.')) return;
    
    setProcessing(true);
    try {
      await api.put(`/admin/registration-requests/${requestId}`, { status: 'approved' });
      fetchRequests();
      fetchStats();
      setSelectedRequest(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.put(`/admin/registration-requests/${selectedRequest.id}`, { 
        status: 'rejected',
        rejection_reason: rejectionReason 
      });
      fetchRequests();
      fetchStats();
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (tableMissing) {
    return (
      <div className="space-y-6" data-testid="registration-requests-page">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Registration Requests</h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">Table Not Found</h3>
              <p className="text-yellow-700 mt-1">The registration_requests table doesn't exist in your database. Run this SQL in Supabase to create it:</p>
              <pre className="mt-4 bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">{setupSql}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registration-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Registration Requests</h1>
        <button onClick={() => { fetchRequests(); fetchStats(); }} className="flex items-center gap-2 px-4 py-2 bg-[#3FA9F5] text-white rounded-lg hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total Requests</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Approved</p>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, phone, email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5] focus:border-transparent"
            data-testid="registration-search-input"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5]"
          data-testid="registration-status-filter"
        >
          <option value="">All (Pending & Rejected)</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner />
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No registration requests found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Restaurant</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Owner</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Submitted</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{req.restaurant_name}</p>
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{req.address}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{req.owner_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{req.phone}</p>
                      <p className="text-sm text-gray-500">{req.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(req.status)}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(req.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedRequest(req)}
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                          data-testid={`view-request-${req.id}`}
                        >
                          View
                        </button>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(req.id)}
                              disabled={processing}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200 rounded-lg disabled:opacity-50"
                              data-testid={`approve-request-${req.id}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => { setSelectedRequest(req); setShowRejectModal(true); }}
                              disabled={processing}
                              className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-lg disabled:opacity-50"
                              data-testid={`reject-request-${req.id}`}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded-lg disabled:opacity-50">Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded-lg disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Details Modal */}
      {selectedRequest && !showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold">Registration Request Details</h2>
              <button onClick={() => setSelectedRequest(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Restaurant Name</label>
                  <p className="font-medium">{selectedRequest.restaurant_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Owner Name</label>
                  <p className="font-medium">{selectedRequest.owner_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="font-medium">{selectedRequest.email || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500">Address</label>
                  <p className="font-medium">{selectedRequest.address || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">FSSAI License</label>
                  <p className="font-medium">{selectedRequest.fssai_license || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">GST Number</label>
                  <p className="font-medium">{selectedRequest.gst_number || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Latitude</label>
                  <p className="font-medium">{selectedRequest.latitude || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Longitude</label>
                  <p className="font-medium">{selectedRequest.longitude || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(selectedRequest.status)}`}>
                    {selectedRequest.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Submitted</label>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                {selectedRequest.reviewed_at && (
                  <>
                    <div>
                      <label className="text-sm text-gray-500">Reviewed At</label>
                      <p className="font-medium">{formatDate(selectedRequest.reviewed_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Reviewed By</label>
                      <p className="font-medium">{selectedRequest.reviewed_by || '-'}</p>
                    </div>
                  </>
                )}
                {selectedRequest.rejection_reason && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-500">Rejection Reason</label>
                    <p className="font-medium text-red-600">{selectedRequest.rejection_reason}</p>
                  </div>
                )}
              </div>
              
              {selectedRequest.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleApprove(selectedRequest.id)}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Approve & Create Restaurant'}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold">Reject Registration</h2>
              <p className="text-gray-500 mt-1">Provide a reason for rejecting {selectedRequest.restaurant_name}</p>
            </div>
            <div className="p-6">
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full h-32 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5] resize-none"
                data-testid="rejection-reason-input"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid="confirm-reject-btn"
                >
                  {processing ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Restaurants Page
const Restaurants = ({ api }) => {
  const [allRestaurants, setAllRestaurants] = useState([]); // Store all fetched data
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, suspended
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [summary, setSummary] = useState({ total: 0, active: 0, suspended: 0 });

  // Fetch data from API (only once or on refresh)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics?data_type=restaurants&time_range=all`);
      const data = res.data.restaurants || [];
      setAllRestaurants(data);
    } catch (err) {
      console.error('Fetch failed:', err);
      setAllRestaurants([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Apply filters locally whenever search/statusFilter/allRestaurants change
  useEffect(() => {
    let filtered = [...allRestaurants];
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(r => 
        (r.name || '').toLowerCase().includes(searchLower) ||
        (r.cuisine || '').toLowerCase().includes(searchLower) ||
        (r.address || '').toLowerCase().includes(searchLower) ||
        (r.email || '').toLowerCase().includes(searchLower) ||
        (r.phone || '').toLowerCase().includes(searchLower) ||
        (r.owner_name || '').toLowerCase().includes(searchLower) ||
        (r.contact || '').toLowerCase().includes(searchLower)
      );
    }
    
    setRestaurants(filtered);
    setSummary({
      total: filtered.length,
      active: filtered.filter(r => r.status === 'active').length,
      suspended: filtered.filter(r => r.status === 'suspended').length
    });
  }, [allRestaurants, search, statusFilter]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (restaurantId, newStatus, restaurantName) => {
    const actionText = newStatus === 'suspended' ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${actionText} "${restaurantName}"?`)) return;
    
    setProcessing(restaurantId);
    try {
      await api.put(`/admin/r/${restaurantId}/status`, { status: newStatus });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${actionText} restaurant`);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (restaurantId, restaurantName) => {
    if (!window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${restaurantName}"?\n\nThis action cannot be undone!`)) return;
    if (!window.confirm(`Final confirmation: Delete "${restaurantName}" and all associated data?`)) return;
    
    setProcessing(restaurantId);
    try {
      await api.delete(`/admin/r/${restaurantId}`);
      fetchData();
      alert(`Restaurant "${restaurantName}" has been deleted.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete restaurant');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="restaurants-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Restaurants ({restaurants.length})</h2>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div 
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'all' ? 'border-[#3FA9F5] ring-2 ring-[#3FA9F5]/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Total Restaurants</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total || restaurants.length}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('active')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{summary.active || 0}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('suspended')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${statusFilter === 'suspended' ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-gray-100 hover:border-gray-200'}`}
        >
          <p className="text-sm text-gray-500">Suspended</p>
          <p className="text-2xl font-bold text-orange-600">{summary.suspended || 0}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, owner..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="restaurant-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
          data-testid="restaurant-status-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>This page shows approved restaurants only. New applications are managed in <strong>Registration Requests</strong>.</span>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : restaurants.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No restaurants found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {restaurants.map((r) => {
            const status = r.status || 'active';
            const isActive = status === 'active';
            const isSuspended = status === 'suspended';
            
            return (
              <div key={r.id} className={`bg-white rounded-xl p-4 shadow-sm border ${isSuspended ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'}`}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Store className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{r.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${isActive ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {isActive ? '✓ Active' : '⏸ Suspended'}
                      </span>
                      {r.is_online && <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">🟢 Online</span>}
                    </div>
                    <p className="text-sm text-gray-500">{r.cuisine}</p>
                    <p className="text-sm text-gray-400 truncate">{r.address}</p>
                    {r.poc_phone && (
                      <p className="text-sm text-blue-600 mt-1">📞 POC: {r.poc_phone}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-6 text-center">
                    <div>
                      <p className="text-xl font-bold text-gray-900">{r.total_orders}</p>
                      <p className="text-xs text-gray-500">Orders</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-600">{r.delivered_orders}</p>
                      <p className="text-xs text-gray-500">Delivered</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#3FA9F5]">₹{r.total_revenue?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Revenue</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-purple-600">{r.completion_rate}%</p>
                      <p className="text-xs text-gray-500">Completion</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => setSelectedRestaurant(r)}
                      className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]"
                      data-testid={`view-restaurant-${r.id}`}
                    >
                      View
                    </button>
                    {isActive ? (
                      <button
                        onClick={() => handleStatusChange(r.id, 'suspended', r.name)}
                        disabled={processing === r.id}
                        className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm hover:bg-orange-200 disabled:opacity-50"
                        data-testid={`suspend-restaurant-${r.id}`}
                      >
                        {processing === r.id ? '...' : 'Suspend'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(r.id, 'active', r.name)}
                        disabled={processing === r.id}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 disabled:opacity-50"
                        data-testid={`reactivate-restaurant-${r.id}`}
                      >
                        {processing === r.id ? '...' : 'Reactivate'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(r.id, r.name)}
                      disabled={processing === r.id}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 disabled:opacity-50"
                      data-testid={`delete-restaurant-${r.id}`}
                    >
                      {processing === r.id ? '...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Restaurant Details Modal */}
      {selectedRestaurant && (
        <RestaurantDetailsModal 
          api={api} 
          restaurant={selectedRestaurant} 
          onClose={() => setSelectedRestaurant(null)}
          onUpdate={() => fetchData()}
        />
      )}
    </div>
  );
};

// Bank Details Section Component (reusable for restaurants and riders)
const BankDetailsSection = ({ api, entityType, entityId, entityData, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [bankData, setBankData] = useState({
    account_holder_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    pan_number: '',
    pan_image_url: ''
  });

  // Initialize bank data from entity
  useEffect(() => {
    if (entityData) {
      setBankData({
        account_holder_name: entityData.account_holder_name || '',
        bank_account_number: entityData.bank_account_number || '',
        bank_ifsc: entityData.bank_ifsc || '',
        pan_number: entityData.pan_number || '',
        pan_image_url: entityData.pan_image_url || ''
      });
    }
  }, [entityData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = entityType === 'restaurant' 
        ? `/admin/restaurants/${entityId}/bank-details`
        : `/admin/riders/${entityId}/bank-details`;
      
      await api.put(endpoint, bankData);
      setEditMode(false);
      if (onUpdate) onUpdate();
      alert('✅ Bank details saved successfully!');
    } catch (err) {
      console.error('Error saving bank details:', err);
      alert('❌ Error saving bank details: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSyncToCashfree = async () => {
    // Validate required fields
    if (!bankData.bank_account_number || !bankData.bank_ifsc) {
      alert('⚠️ Please save bank account number and IFSC code before syncing to Cashfree');
      return;
    }

    setSyncing(true);
    try {
      const res = await api.post('/admin/cashfree/sync-vendor', {
        entity_type: entityType,
        entity_id: entityId
      });
      
      if (res.data.success) {
        alert(`✅ ${res.data.message}\n\nVendor ID: ${res.data.vendor_id}\nEnvironment: ${res.data.environment}\nVerification Status: ${res.data.verification_status}`);
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error syncing to Cashfree:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      alert('❌ Error syncing to Cashfree:\n' + errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const maskAccountNumber = (num) => {
    if (!num || num.length < 4) return num || 'N/A';
    return '••••••••' + num.slice(-4);
  };

  const getVerificationStatusBadge = (status) => {
    if (!status) return null;
    const statusMap = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '⏳', label: 'Pending' },
      'verified': { bg: 'bg-green-100', text: 'text-green-700', icon: '✅', label: 'Verified' },
      'failed': { bg: 'bg-red-100', text: 'text-red-700', icon: '❌', label: 'Failed' }
    };
    const s = statusMap[status.toLowerCase()] || statusMap['pending'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${s.bg} ${s.text}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  return (
    <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200" data-testid="bank-details-section">
      <div className="flex items-center justify-between border-b border-emerald-200 pb-2 mb-4">
        <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
          <Landmark className="w-4 h-4" />
          Bank & Payment Details
        </h3>
        {!editMode ? (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center gap-1 px-3 py-1 text-sm text-emerald-700 hover:bg-emerald-100 rounded-lg transition"
            data-testid="edit-bank-details-btn"
          >
            <Edit3 className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <button
            onClick={() => setEditMode(false)}
            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bank Details Column */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-emerald-800">Bank Account Details</h4>
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Account Holder Name</label>
            {editMode ? (
              <input
                type="text"
                value={bankData.account_holder_name}
                onChange={(e) => setBankData({...bankData, account_holder_name: e.target.value})}
                placeholder="Enter account holder name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="account-holder-name-input"
              />
            ) : (
              <p className="text-gray-900 text-sm">{entityData?.account_holder_name || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Bank Account Number</label>
            {editMode ? (
              <input
                type="text"
                value={bankData.bank_account_number}
                onChange={(e) => setBankData({...bankData, bank_account_number: e.target.value.replace(/\D/g, '')})}
                placeholder="Enter account number"
                maxLength={18}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="bank-account-number-input"
              />
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-gray-900 text-sm font-mono">
                  {showAccountNumber ? (entityData?.bank_account_number || 'N/A') : maskAccountNumber(entityData?.bank_account_number)}
                </p>
                {entityData?.bank_account_number && (
                  <button
                    onClick={() => setShowAccountNumber(!showAccountNumber)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showAccountNumber ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">IFSC Code</label>
            {editMode ? (
              <input
                type="text"
                value={bankData.bank_ifsc}
                onChange={(e) => setBankData({...bankData, bank_ifsc: e.target.value.toUpperCase()})}
                placeholder="e.g., SBIN0001234"
                maxLength={11}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="bank-ifsc-input"
              />
            ) : (
              <p className="text-gray-900 text-sm font-mono">{entityData?.bank_ifsc || 'N/A'}</p>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">PAN Number</label>
            {editMode ? (
              <input
                type="text"
                value={bankData.pan_number}
                onChange={(e) => setBankData({...bankData, pan_number: e.target.value.toUpperCase()})}
                placeholder="e.g., ABCDE1234F"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="pan-number-input"
              />
            ) : (
              <p className="text-gray-900 text-sm font-mono">{entityData?.pan_number || 'N/A'}</p>
            )}
          </div>
        </div>

        {/* Cashfree Status Column */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-emerald-800">Cashfree Integration</h4>
          
          <div className="p-4 bg-white rounded-lg border border-emerald-100 space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Fleety ID</label>
              <p className="text-gray-900 text-sm font-mono font-medium">
                {entityData?.fleety_id || <span className="text-gray-400 italic">Not assigned</span>}
              </p>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Cashfree Vendor ID</label>
              <p className="text-gray-900 text-sm font-mono">
                {entityData?.cashfree_vendor_id || <span className="text-gray-400 italic">Not synced</span>}
              </p>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Verification Status</label>
              {entityData?.cashfree_vendor_id ? (
                getVerificationStatusBadge(entityData?.bank_verification_status)
              ) : (
                <span className="text-gray-400 text-sm italic">Sync required</span>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Last Synced</label>
              <p className="text-gray-500 text-sm">
                {entityData?.cashfree_synced_at 
                  ? new Date(entityData.cashfree_synced_at).toLocaleString('en-IN', { 
                      day: '2-digit', month: 'short', year: 'numeric', 
                      hour: '2-digit', minute: '2-digit' 
                    })
                  : 'Never'
                }
              </p>
            </div>
          </div>

          {/* Sync Button */}
          {!editMode && (
            <button
              onClick={handleSyncToCashfree}
              disabled={syncing || !entityData?.bank_account_number || !entityData?.bank_ifsc}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                !entityData?.bank_account_number || !entityData?.bank_ifsc
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
              data-testid="sync-cashfree-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  {entityData?.cashfree_vendor_id ? 'Re-sync to Cashfree' : 'Sync to Cashfree'}
                </>
              )}
            </button>
          )}

          {!entityData?.bank_account_number && !editMode && (
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              ⚠️ Add bank details before syncing to Cashfree
            </p>
          )}
        </div>
      </div>

      {/* Save Button (Edit Mode) */}
      {editMode && (
        <div className="mt-6 pt-4 border-t border-emerald-200 flex justify-end gap-3">
          <button
            onClick={() => setEditMode(false)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            data-testid="save-bank-details-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Bank Details
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Restaurant Details Modal Component (with Details + Menu tabs)
const RestaurantDetailsModal = ({ api, restaurant, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [restaurantData, setRestaurantData] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/restaurants/${restaurant.id}/menu?_t=${Date.now()}`);
      setRestaurantData(res.data.restaurant);
      setMenuData(res.data.menu);
      setEditData(res.data.restaurant);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, restaurant.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/restaurants/${restaurant.id}`, editData);
      setEditMode(false);
      fetchData();
      onUpdate();
      alert('Restaurant updated successfully!');
    } catch (err) {
      alert('Error updating restaurant');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (itemId, file) => {
    if (!file) return;
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to Supabase storage via our API
      const uploadRes = await api.post('/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.success) {
        // Update menu item with new image URL
        await api.put(`/admin/menu-items/${itemId}/image`, {
          image_url: uploadRes.data.url
        });
        fetchData();
        alert('Image updated successfully!');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      // Fallback: prompt for URL if upload fails
      const newUrl = window.prompt('Image upload requires Supabase Storage setup.\n\nEnter image URL instead:', '');
      if (newUrl) {
        try {
          await api.put(`/admin/menu-items/${itemId}/image`, { image_url: newUrl });
          fetchData();
          alert('Image URL updated successfully!');
        } catch (e) {
          alert('Error updating image URL');
        }
      }
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const items = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 3) {
            items.push({
              name: values[headers.indexOf('name')] || values[0],
              description: values[headers.indexOf('description')] || values[1] || '',
              price: parseFloat(values[headers.indexOf('price')] || values[2]) || 0,
              category: values[headers.indexOf('category')] || values[3] || 'Main Course',
              is_veg: (values[headers.indexOf('is_veg')] || values[4] || 'true').toLowerCase() === 'true',
              is_bestseller: (values[headers.indexOf('is_bestseller')] || values[5] || 'false').toLowerCase() === 'true'
            });
          }
        }
        setUploadData(items);
        setUploadMode(true);
      } catch (err) {
        alert('Error parsing file. Please ensure it is a valid CSV.');
      }
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (uploadData.length === 0) return;
    setUploading(true);
    try {
      await api.post(`/admin/restaurants/${restaurant.id}/menu/bulk`, { items: uploadData });
      setUploadMode(false);
      setUploadData([]);
      fetchData();
      alert('Menu uploaded successfully!');
    } catch (err) {
      alert('Error uploading menu');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/admin/restaurants/${restaurant.id}/menu/${itemId}`);
      fetchData();
    } catch (err) {
      alert('Error deleting item');
    }
  };

  const handleDeleteRestaurant = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/restaurants/${restaurant.id}`);
      if (res.data.success) {
        alert(`✅ ${res.data.message}\n\nDeleted:\n- Menu Items: ${res.data.deleted.menu_items}\n- Orders: ${res.data.deleted.orders_deleted}\n- Support Tickets: ${res.data.deleted.support_tickets}`);
        onClose();
        onUpdate();
      }
    } catch (err) {
      alert('Error deleting restaurant: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden">
                {restaurantData?.image_url ? (
                  <img src={restaurantData.image_url} alt={restaurantData.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{restaurantData?.name || restaurant.name}</h2>
                <p className="text-sm text-gray-500">{restaurantData?.cuisine}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-yellow-600">⭐ {restaurantData?.rating || 0}</span>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-sm text-gray-500">{restaurantData?.delivery_time}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === 'details' 
                  ? 'bg-[#3FA9F5] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Restaurant Details
            </button>
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === 'menu' 
                  ? 'bg-[#3FA9F5] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Menu ({menuData?.total_items || 0} items)
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
          {loading ? (
            <LoadingSpinner />
          ) : activeTab === 'details' ? (
            <div className="space-y-6">
              {/* Edit Toggle */}
              <div className="flex justify-between">
                <button 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                >
                  🗑️ Delete Restaurant
                </button>
                {editMode ? (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => { setEditMode(false); setEditData(restaurantData); }}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm"
                  >
                    ✏️ Edit Details
                  </button>
                )}
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
                  <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Restaurant?</h3>
                      <p className="text-gray-600 mb-4">
                        You are about to delete <strong>{restaurantData?.name}</strong> and all related data.
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-6">
                        <p className="text-red-800 font-semibold mb-2">⚠️ This action will permanently delete:</p>
                        <ul className="text-red-700 text-sm space-y-1">
                          <li>• All menu items ({menuData?.total_items || 0} items)</li>
                          <li>• All related orders</li>
                          <li>• All support tickets</li>
                          <li>• All dispute records</li>
                        </ul>
                        <p className="text-red-800 font-bold mt-3">⛔ THIS CANNOT BE UNDONE!</p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteRestaurant}
                          disabled={deleting}
                          className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          {deleting ? 'Deleting...' : 'Yes, Delete Forever'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Restaurant Details Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Restaurant Name</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.name || ''}
                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-gray-900">{restaurantData?.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Cuisine Type</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.cuisine || ''}
                        onChange={(e) => setEditData({...editData, cuisine: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        placeholder="e.g., Italian, Chinese, Indian"
                      />
                    ) : (
                      <p className="text-gray-900">{restaurantData?.cuisine}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Rating</label>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editData.rating || ''}
                          onChange={(e) => setEditData({...editData, rating: parseFloat(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                      ) : (
                        <p className="text-gray-900">⭐ {restaurantData?.rating}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Delivery Time</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.delivery_time || ''}
                          onChange={(e) => setEditData({...editData, delivery_time: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="e.g., 30-40 min"
                        />
                      ) : (
                        <p className="text-gray-900">{restaurantData?.delivery_time}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Contact Number</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.contact || ''}
                        onChange={(e) => setEditData({...editData, contact: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-gray-900">{restaurantData?.contact || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      📞 POC Phone Number
                      <span className="text-xs text-blue-500 ml-2">(For ops issue resolution)</span>
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.poc_phone || ''}
                        onChange={(e) => setEditData({...editData, poc_phone: e.target.value})}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-blue-50"
                        placeholder="This number should always be working"
                      />
                    ) : (
                      <p className="text-gray-900 font-medium">{restaurantData?.poc_phone || 'Not set'}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Use this number to contact restaurant for quick issue resolution</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Image URL</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.image_url || ''}
                        onChange={(e) => setEditData({...editData, image_url: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-gray-900 text-sm truncate">{restaurantData?.image_url || 'N/A'}</p>
                    )}
                  </div>
                </div>

                {/* Location Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 border-b pb-2">Location Details</h3>

                  {/* Approval Status */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Approval Status</label>
                    {editMode ? (
                      <select
                        value={editData.approval_status || 'pending'}
                        onChange={(e) => setEditData({...editData, approval_status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      >
                        <option value="pending">⏳ Pending Approval</option>
                        <option value="approved">✓ Approved</option>
                        <option value="on_hold">⏸ On Hold</option>
                        <option value="removed">✕ Removed</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        restaurantData?.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                        restaurantData?.approval_status === 'on_hold' ? 'bg-orange-100 text-orange-700' :
                        restaurantData?.approval_status === 'removed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {restaurantData?.approval_status === 'approved' ? '✓ Approved' :
                         restaurantData?.approval_status === 'on_hold' ? '⏸ On Hold' :
                         restaurantData?.approval_status === 'removed' ? '✕ Removed' :
                         '⏳ Pending Approval'}
                      </span>
                    )}
                    {restaurantData?.status_message && (
                      <p className="text-xs text-red-500 mt-2">Message: {restaurantData.status_message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Full Address</label>
                    {editMode ? (
                      <textarea
                        value={editData.address || ''}
                        onChange={(e) => setEditData({...editData, address: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        rows={3}
                      />
                    ) : (
                      <p className="text-gray-900 text-sm">{restaurantData?.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Pincode</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.pincode || ''}
                          onChange={(e) => setEditData({...editData, pincode: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                      ) : (
                        <p className="text-gray-900">{restaurantData?.pincode}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Plus Code</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.plus_code || ''}
                          onChange={(e) => setEditData({...editData, plus_code: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                      ) : (
                        <p className="text-gray-900">{restaurantData?.plus_code || 'N/A'}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Latitude</label>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={editData.lat || ''}
                          onChange={(e) => setEditData({...editData, lat: parseFloat(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                      ) : (
                        <p className="text-gray-900">{restaurantData?.lat}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Longitude</label>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={editData.lng || ''}
                          onChange={(e) => setEditData({...editData, lng: parseFloat(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                      ) : (
                        <p className="text-gray-900">{restaurantData?.lng}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Restaurant ID</label>
                    <p className="text-gray-500 text-sm font-mono">{restaurantData?.id}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Created At</label>
                    <p className="text-gray-500 text-sm">{formatDate(restaurantData?.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Registration & Partner Information */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-900 border-b border-blue-200 pb-2 mb-4">📋 Registration & Partner Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Registration Details */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-blue-800">Registration Details</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500">Owner Name</label>
                        {editMode ? (
                          <input
                            type="text"
                            value={editData.owner_name || restaurantData?.registration_info?.owner_name || ''}
                            onChange={(e) => setEditData({...editData, owner_name: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                          />
                        ) : (
                          <p className="text-gray-900 text-sm">{restaurantData?.registration_info?.owner_name || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Email</label>
                        {editMode ? (
                          <input
                            type="email"
                            value={editData.partner_email || restaurantData?.registration_info?.email || restaurantData?.partner_info?.email || ''}
                            onChange={(e) => setEditData({...editData, partner_email: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                          />
                        ) : (
                          <p className="text-gray-900 text-sm">{restaurantData?.registration_info?.email || restaurantData?.partner_info?.email || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Phone</label>
                        {editMode ? (
                          <input
                            type="text"
                            value={editData.partner_phone || restaurantData?.registration_info?.phone || restaurantData?.partner_info?.phone || ''}
                            onChange={(e) => setEditData({...editData, partner_phone: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                          />
                        ) : (
                          <p className="text-gray-900 text-sm">{restaurantData?.registration_info?.phone || restaurantData?.partner_info?.phone || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">FSSAI License</label>
                        {editMode ? (
                          <input
                            type="text"
                            value={editData.fssai_license || restaurantData?.registration_info?.fssai_license || ''}
                            onChange={(e) => setEditData({...editData, fssai_license: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                          />
                        ) : (
                          <p className="text-gray-900 text-sm">{restaurantData?.registration_info?.fssai_license || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">GST Number</label>
                        {editMode ? (
                          <input
                            type="text"
                            value={editData.gst_number || restaurantData?.registration_info?.gst_number || ''}
                            onChange={(e) => setEditData({...editData, gst_number: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                          />
                        ) : (
                          <p className="text-gray-900 text-sm">{restaurantData?.registration_info?.gst_number || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Registered At</label>
                        <p className="text-gray-900 text-sm">{formatDate(restaurantData?.registration_info?.registered_at)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Partner Login Info */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-blue-800">Partner Login Info</h4>
                    {restaurantData?.partner_info || editMode ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500">Login Phone (OTP)</label>
                          {editMode ? (
                            <input
                              type="text"
                              value={editData.partner_phone || restaurantData?.partner_info?.phone || ''}
                              onChange={(e) => setEditData({...editData, partner_phone: e.target.value})}
                              className="w-full px-2 py-1 border border-blue-300 rounded text-sm bg-blue-50"
                              placeholder="Phone for OTP login"
                            />
                          ) : (
                            <p className="text-gray-900 text-sm font-medium">{restaurantData?.partner_info?.phone || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Login Email</label>
                          {editMode ? (
                            <input
                              type="email"
                              value={editData.partner_email || restaurantData?.partner_info?.email || ''}
                              onChange={(e) => setEditData({...editData, partner_email: e.target.value})}
                              className="w-full px-2 py-1 border border-blue-300 rounded text-sm bg-blue-50"
                              placeholder="Email for partner"
                            />
                          ) : (
                            <p className="text-gray-900 text-sm">{restaurantData?.partner_info?.email || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Account Status</label>
                          {editMode ? (
                            <select
                              value={editData.partner_is_active !== undefined ? editData.partner_is_active : (restaurantData?.partner_info?.is_active ? 'true' : 'false')}
                              onChange={(e) => setEditData({...editData, partner_is_active: e.target.value})}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            >
                              <option value="true">✓ Active</option>
                              <option value="false">✕ Inactive</option>
                            </select>
                          ) : (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              restaurantData?.partner_info?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {restaurantData?.partner_info?.is_active ? '✓ Active' : '✕ Inactive'}
                            </span>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Partner ID</label>
                          <p className="text-gray-500 text-xs font-mono">{restaurantData?.partner_info?.id || 'Will be created on save'}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-800 text-sm">⚠️ No partner account found. Click "Edit Details" to create one.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bank & Payment Details Section */}
              <BankDetailsSection 
                api={api} 
                entityType="restaurant" 
                entityId={restaurant.id}
                entityData={restaurantData}
                onUpdate={fetchData}
              />
            </div>
          ) : (
            /* Menu Tab Content */
            <div className="space-y-4">
              {/* Upload Button */}
              <div className="flex justify-end">
                <label className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 cursor-pointer">
                  <input type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileUpload} />
                  📤 Upload Menu (CSV)
                </label>
              </div>

              {uploadMode ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Preview Upload ({uploadData.length} items)</h3>
                    <div className="flex gap-2">
                      <button onClick={() => { setUploadMode(false); setUploadData([]); }} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">
                        Cancel
                      </button>
                      <button onClick={handleBulkUpload} disabled={uploading} className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm disabled:opacity-50">
                        {uploading ? 'Uploading...' : 'Confirm Upload'}
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="pb-2">Name</th>
                          <th className="pb-2">Category</th>
                          <th className="pb-2">Price</th>
                          <th className="pb-2">Veg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadData.map((item, i) => (
                          <tr key={i} className="border-t border-gray-200">
                            <td className="py-2">{item.name}</td>
                            <td className="py-2">{item.category}</td>
                            <td className="py-2">₹{item.price}</td>
                            <td className="py-2">{item.is_veg ? '🟢' : '🔴'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">
                    CSV Format: name, description, price, category, is_veg (true/false), is_bestseller (true/false)
                  </p>
                </div>
              ) : menuData?.categories && Object.keys(menuData.categories).length > 0 ? (
                <div className="space-y-6">
                  {Object.entries(menuData.categories).map(([category, items]) => (
                    <div key={category}>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#3FA9F5] rounded-full"></span>
                        {category} ({items.length})
                      </h3>
                      <div className="grid gap-3">
                        {items.map((item) => (
                          <div key={item.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="relative w-16 h-16 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0 group">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-2xl">
                                  {item.is_veg ? '🥬' : '🍖'}
                                </div>
                              )}
                              {/* Image Upload Overlay */}
                              <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                                <input 
                                  type="file" 
                                  accept="image/*" 
                                  className="hidden" 
                                  onChange={(e) => handleImageUpload(item.id, e.target.files[0])}
                                />
                                <span className="text-white text-xs font-medium">📷 Edit</span>
                              </label>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`w-4 h-4 border-2 ${item.is_veg ? 'border-green-600' : 'border-red-600'} flex items-center justify-center`}>
                                  <span className={`w-2 h-2 rounded-full ${item.is_veg ? 'bg-green-600' : 'bg-red-600'}`}></span>
                                </span>
                                <h4 className="font-medium text-gray-900">{item.name}</h4>
                                {item.is_bestseller && (
                                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">⭐ Bestseller</span>
                                )}
                                {!item.image_url && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs">No Image</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1 line-clamp-1">{item.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">₹{item.price}</p>
                              <div className="flex gap-2 mt-1">
                                <label className="text-xs text-blue-500 hover:underline cursor-pointer">
                                  <input 
                                    type="file" 
                                    accept="image/*" 
                                    className="hidden" 
                                    onChange={(e) => handleImageUpload(item.id, e.target.files[0])}
                                  />
                                  📷 Image
                                </label>
                                <button onClick={() => handleDeleteItem(item.id)} className="text-xs text-red-500 hover:underline">
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No menu items found</p>
                  <p className="text-sm text-gray-400 mt-2">Upload a CSV file to add menu items</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Rider Approvals Page
const RiderApprovals = ({ api, onApprovalChange }) => {
  const [riders, setRiders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedRider, setSelectedRider] = useState(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [documents, setDocuments] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRiders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('approval_status', filter);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 20);
      
      const res = await api.get(`/admin/riders/all?${params.toString()}`);
      setRiders(res.data.riders || []);
      setStats(res.data.stats || { total: 0, pending: 0, approved: 0, rejected: 0 });
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, search, page]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  const handleViewDocuments = async (rider) => {
    setSelectedRider(rider);
    try {
      const res = await api.get(`/admin/riders/${rider.id}/documents`);
      setDocuments(res.data.documents || {});
      setShowDocuments(true);
    } catch (err) {
      console.error(err);
      setDocuments({});
      setShowDocuments(true);
    }
  };

  const handleApprove = async (riderId) => {
    if (!window.confirm('Approve this rider?')) return;
    setProcessing(true);
    try {
      await api.put(`/admin/riders/${riderId}/approval`, { approval_status: 'approved' });
      fetchRiders();
      if (onApprovalChange) onApprovalChange();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve rider');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await api.put(`/admin/riders/${selectedRider.id}/approval`, {
        approval_status: 'rejected',
        rejection_reason: rejectionReason
      });
      setShowRejectModal(false);
      setSelectedRider(null);
      setRejectionReason('');
      fetchRiders();
      if (onApprovalChange) onApprovalChange();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject rider');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="rider-approvals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Rider Approvals</h1>
        <button onClick={fetchRiders} className="flex items-center gap-2 px-4 py-2 bg-[#3FA9F5] text-white rounded-lg hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total Riders</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 cursor-pointer hover:shadow-md" onClick={() => setFilter('pending')}>
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 cursor-pointer hover:shadow-md" onClick={() => setFilter('approved')}>
          <p className="text-sm text-gray-500">Approved</p>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 cursor-pointer hover:shadow-md" onClick={() => setFilter('rejected')}>
          <p className="text-sm text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, phone, email, vehicle..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5]"
            data-testid="rider-search-input"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5]"
          data-testid="rider-status-filter"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner />
      ) : riders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Bike className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No riders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rider</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Vehicle</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Registered</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {riders.map((rider) => (
                  <tr key={rider.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          {rider.profile_photo ? (
                            <img src={rider.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{rider.full_name || rider.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">ID: {rider.id?.substring(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{rider.phone}</p>
                      <p className="text-sm text-gray-500">{rider.email || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900 capitalize">{rider.vehicle_type || '-'}</p>
                      <p className="text-sm text-gray-500">{rider.vehicle_number || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(rider.approval_status || 'pending')}`}>
                        {rider.approval_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-sm">{formatDate(rider.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDocuments(rider)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg"
                          data-testid={`view-docs-${rider.id}`}
                        >
                          Documents
                        </button>
                        {(rider.approval_status === 'pending' || !rider.approval_status) && (
                          <>
                            <button
                              onClick={() => handleApprove(rider.id)}
                              disabled={processing}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200 rounded-lg disabled:opacity-50"
                              data-testid={`approve-rider-${rider.id}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => { setSelectedRider(rider); setShowRejectModal(true); }}
                              disabled={processing}
                              className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-lg disabled:opacity-50"
                              data-testid={`reject-rider-${rider.id}`}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded-lg disabled:opacity-50">Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded-lg disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents Modal */}
      {showDocuments && selectedRider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Rider Documents</h2>
                <p className="text-gray-500">{selectedRider.full_name || selectedRider.name}</p>
              </div>
              <button onClick={() => { setShowDocuments(false); setSelectedRider(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {Object.keys(documents).length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No documents uploaded</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(documents).map(([key, url]) => (
                    <div key={key} className="border rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={url} alt={key} className="w-full h-32 object-cover rounded-lg bg-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
                        <span className="text-sm text-blue-600 hover:underline mt-2 block">View Full</span>
                      </a>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Rider Details */}
              <div className="mt-6 pt-6 border-t grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Full Name</label>
                  <p className="font-medium">{selectedRider.full_name || selectedRider.name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium">{selectedRider.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="font-medium">{selectedRider.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Vehicle Type</label>
                  <p className="font-medium capitalize">{selectedRider.vehicle_type || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Vehicle Number</label>
                  <p className="font-medium">{selectedRider.vehicle_number || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(selectedRider.approval_status || 'pending')}`}>
                    {selectedRider.approval_status || 'pending'}
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              {(selectedRider.approval_status === 'pending' || !selectedRider.approval_status) && (
                <div className="flex gap-3 mt-6 pt-6 border-t">
                  <button
                    onClick={() => { handleApprove(selectedRider.id); setShowDocuments(false); }}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Approve Rider'}
                  </button>
                  <button
                    onClick={() => { setShowDocuments(false); setShowRejectModal(true); }}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject Rider
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold">Reject Rider</h2>
              <p className="text-gray-500 mt-1">Provide a reason for rejecting {selectedRider.full_name || selectedRider.name}</p>
            </div>
            <div className="p-6">
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full h-32 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#3FA9F5] resize-none"
                data-testid="rider-rejection-reason"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid="confirm-reject-rider"
                >
                  {processing ? 'Rejecting...' : 'Reject Rider'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Riders Page
const Riders = ({ api, token }) => {
  const [riders, setRiders] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderDetails, setRiderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(null);
  const [adminComments, setAdminComments] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    
    // Retry logic for infrastructure-level hanging issues
    const fetchWithRetry = async (retries = 2) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
          
          const response = await fetch(`${BACKEND_URL}/api/admin/riders/all?page=${page}&limit=20&_t=${Date.now()}&retry=${i}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) throw new Error('Failed to fetch riders');
          return await response.json();
        } catch (err) {
          console.log(`Attempt ${i + 1} failed:`, err.message);
          if (i === retries) throw err;
          await new Promise(r => setTimeout(r, 500)); // Small delay before retry
        }
      }
    };
    
    try {
      const data = await fetchWithRetry();
      
      // Map the response to the expected format including order statistics
      const ridersData = (data.riders || []).map(rider => ({
        id: rider.id,
        name: rider.full_name,
        phone: rider.phone_number || rider.phone,
        email: rider.email,
        vehicle_number: rider.vehicle_number,
        fleety_id: rider.fleety_id,
        status: rider.status || (rider.approval_status === 'approved' ? 'online' : 'offline'),
        // Order statistics from backend
        fleety_orders: rider.fleety_orders || 0,
        fleetygo_orders: rider.fleetygo_orders || 0,
        total_delivered: rider.total_delivered || 0,
        total_earnings: rider.total_earnings || 0,
        last_active: rider.last_active,
        // Bank details
        account_holder_name: rider.account_holder_name,
        bank_account_number: rider.bank_account_number,
        bank_ifsc: rider.bank_ifsc,
        cashfree_vendor_id: rider.cashfree_vendor_id,
        bank_verification_status: rider.bank_verification_status,
        approval_status: rider.approval_status
      }));
      
      // Filter by status if needed
      let filteredRiders = ridersData;
      if (statusFilter !== "all") {
        filteredRiders = ridersData.filter(r => r.status === statusFilter);
      }
      
      setRiders(filteredRiders);
      setSummary({
        total_riders: ridersData.length,
        online: ridersData.filter(r => r.status === 'online').length,
        offline: ridersData.filter(r => r.status === 'offline').length
      });
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching riders:', err);
      // On timeout/error, try to show empty state rather than infinite loading
      setRiders([]);
      setSummary({ total_riders: 0, online: 0, offline: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, token]);

  const fetchRiderDetails = useCallback(async (riderId) => {
    setLoadingDetails(true);
    try {
      // Fetch rider details from the riders table directly
      const res = await api.get(`/admin/riders/all?_t=${Date.now()}`);
      const rider = res.data.riders?.find(r => r.id === riderId);
      if (rider) {
        setRiderDetails(rider);
      }
    } catch (err) {
      console.error('Error fetching rider details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [api]);

  const handleRiderClick = (rider) => {
    setSelectedRider(rider);
    fetchRiderDetails(rider.id);
  };

  const handleCloseModal = () => {
    setSelectedRider(null);
    setRiderDetails(null);
  };

  const handleStatusChange = async (riderId, newStatus, riderName, comments = null) => {
    setProcessing(true);
    try {
      await api.put(`/admin/riders/${riderId}/status`, { 
        status: newStatus,
        admin_comments: comments
      });
      // Refresh rider details
      fetchRiderDetails(riderId);
      fetchData();
      alert(`Rider "${riderName}" has been ${newStatus === 'active' ? 'reactivated' : 'suspended'}`);
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to update rider status`);
    } finally {
      setProcessing(false);
    }
  };

  const openCommentsModal = (action) => {
    setShowCommentsModal(action);
    setAdminComments(riderDetails?.admin_comments || "");
  };

  const handleCommentsSubmit = async () => {
    if (!showCommentsModal || !selectedRider) return;
    await handleStatusChange(selectedRider.id, showCommentsModal, riderDetails?.full_name || selectedRider.name || 'Rider', adminComments);
    setShowCommentsModal(null);
    setAdminComments("");
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="riders-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Riders</h2>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Total Riders</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total_riders || 0}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-sm text-green-600">Online</p>
          <p className="text-2xl font-bold text-green-600">{summary.online || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Offline</p>
          <p className="text-2xl font-bold text-gray-500">{summary.offline || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or phone..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'online', 'offline'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                statusFilter === status 
                  ? 'bg-[#3FA9F5] text-white' 
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Riders Table */}
      {loading ? (
        <LoadingSpinner />
      ) : riders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Bike className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No riders found</p>
          <p className="text-sm text-gray-400 mt-1">Riders will appear here once they complete deliveries</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Fleety ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Fleety Orders</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">FleetyGo Orders</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Total Delivered</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Earnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {riders.map((rider) => (
                  <tr 
                    key={rider.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRiderClick(rider)}
                    data-testid={`rider-row-${rider.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                        rider.status === 'online' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${rider.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        {rider.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{rider.fleety_id || '-'}</td>
                    <td className="px-4 py-3 font-medium">{rider.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-500">{rider.phone || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {rider.fleety_orders}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        {rider.fleetygo_orders}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{rider.total_delivered}</td>
                    <td className="px-4 py-3 font-medium text-green-600">₹{rider.total_earnings?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rider Details Modal */}
      {selectedRider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div 
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="rider-details-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                  {(selectedRider.name || 'R').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedRider.name || 'Unknown Rider'}</h2>
                  <p className="text-gray-500 text-sm">{selectedRider.phone || 'No phone'}</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Basic Info */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-4">📋 Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">Full Name</label>
                        <p className="font-medium">{riderDetails?.full_name || selectedRider.name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Phone Number</label>
                        <p className="font-medium">{riderDetails?.phone_number || selectedRider.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p className="font-medium">{riderDetails?.email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Vehicle Type</label>
                        <p className="font-medium">{riderDetails?.vehicle_type || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Vehicle Number</label>
                        <p className="font-medium font-mono">{riderDetails?.vehicle_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">License Number</label>
                        <p className="font-medium font-mono">{riderDetails?.license_number || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Performance Stats */}
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <h3 className="font-semibold text-blue-900 mb-4">📊 Performance</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{selectedRider.fleety_orders || 0}</p>
                        <p className="text-xs text-gray-500">Fleety Orders</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{selectedRider.fleetygo_orders || 0}</p>
                        <p className="text-xs text-gray-500">FleetyGo Orders</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">₹{(selectedRider.total_earnings || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Total Earnings</p>
                      </div>
                    </div>
                  </div>

                  {/* Bank Details Section */}
                  <BankDetailsSection 
                    api={api} 
                    entityType="rider" 
                    entityId={selectedRider.id}
                    entityData={riderDetails}
                    onUpdate={() => fetchRiderDetails(selectedRider.id)}
                  />

                  {/* Account Status Section */}
                  <div className={`p-4 rounded-xl ${riderDetails?.status === 'suspended' ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
                    <h3 className="font-semibold text-gray-900 mb-4">🔐 Account Status</h3>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Current Status</p>
                        <p className={`font-semibold ${riderDetails?.status === 'suspended' ? 'text-orange-600' : 'text-green-600'}`}>
                          {riderDetails?.status === 'suspended' ? '⏸ Suspended' : '✓ Active'}
                        </p>
                      </div>
                      {riderDetails?.status === 'suspended' ? (
                        <button
                          onClick={() => openCommentsModal('active')}
                          disabled={processing}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:opacity-50"
                          data-testid="reactivate-rider-btn"
                        >
                          {processing ? '...' : 'Reactivate'}
                        </button>
                      ) : (
                        <button
                          onClick={() => openCommentsModal('suspended')}
                          disabled={processing}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50"
                          data-testid="suspend-rider-btn"
                        >
                          {processing ? '...' : 'Suspend'}
                        </button>
                      )}
                    </div>
                    {riderDetails?.admin_comments && (
                      <div className="bg-white/50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Admin Comments</p>
                        <p className="text-sm text-gray-700">{riderDetails.admin_comments}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal for Suspend/Reactivate */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setShowCommentsModal(null)}>
          <div 
            className="bg-white rounded-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {showCommentsModal === 'suspended' ? '⏸ Suspend Rider' : '✓ Reactivate Rider'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {showCommentsModal === 'suspended' 
                ? `Are you sure you want to suspend "${riderDetails?.full_name || selectedRider?.name || 'this rider'}"?`
                : `Are you sure you want to reactivate "${riderDetails?.full_name || selectedRider?.name || 'this rider'}"?`
              }
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Comments (optional)</label>
              <textarea
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                placeholder="Add a note about this action..."
                className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCommentsModal(null)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCommentsSubmit}
                disabled={processing}
                className={`flex-1 px-4 py-2 rounded-lg text-sm text-white ${
                  showCommentsModal === 'suspended' 
                    ? 'bg-orange-500 hover:bg-orange-600' 
                    : 'bg-green-500 hover:bg-green-600'
                } disabled:opacity-50`}
              >
                {processing ? '...' : (showCommentsModal === 'suspended' ? 'Suspend' : 'Reactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Profit and Loss Page
const ProfitLoss = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("month");
  const [expandedSections, setExpandedSections] = useState({
    platformFees: false,
    gst: false,
    totalRevenue: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/profit-loss?time_range=${timeRange}&_t=${Date.now()}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingSpinner />;

  const revenue = data?.revenue || {};
  const losses = data?.losses || {};
  const summary = data?.summary || {};
  const chartData = data?.chart_data || [];

  // Collapsible Revenue Row Component
  const RevenueRow = ({ label, description, total, fleety, fleetygo, fleetyOrders, fleetygoOrders, bgColor, textColor, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];
    return (
      <div className={`rounded-lg overflow-hidden ${bgColor}`}>
        <button 
          onClick={() => toggleSection(sectionKey)}
          className="w-full p-3 flex items-center justify-between hover:opacity-90 transition"
        >
          <div className="text-left">
            <p className={`font-medium text-gray-900 flex items-center gap-2`}>
              {label}
              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </p>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          <p className={`text-xl font-bold ${textColor}`}>₹{total?.toLocaleString()}</p>
        </button>
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-white/30">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white/50 rounded p-2">
                <p className="text-blue-600 font-medium">Fleety (Food)</p>
                <p className="text-lg font-bold text-blue-800">₹{fleety?.toLocaleString()}</p>
                {fleetyOrders !== undefined && <p className="text-xs text-gray-500">{fleetyOrders} orders</p>}
              </div>
              <div className="bg-white/50 rounded p-2">
                <p className="text-green-600 font-medium">FleetyGo (Package)</p>
                <p className="text-lg font-bold text-green-800">₹{fleetygo?.toLocaleString()}</p>
                {fleetygoOrders !== undefined && <p className="text-xs text-gray-500">{fleetygoOrders} orders</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="profit-loss-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Profit & Loss</h2>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Timeline Filter */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 mr-2">Time Period:</span>
          {[
            { value: "today", label: "Today" },
            { value: "week", label: "This Week" },
            { value: "month", label: "This Month" },
            { value: "all", label: "All Time" }
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeRange(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === option.value
                  ? "bg-[#3FA9F5] text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">Showing data for: <span className="font-medium text-gray-600">{data?.range_label}</span></p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <p className="text-green-100 text-sm">Gross Revenue</p>
          <p className="text-3xl font-bold">₹{summary.gross_revenue?.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
          <p className="text-red-100 text-sm">Total Losses</p>
          <p className="text-3xl font-bold">₹{summary.total_losses?.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <p className="text-blue-100 text-sm">Net Profit</p>
          <p className="text-3xl font-bold">₹{summary.net_profit?.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <p className="text-purple-100 text-sm">Profit Margin</p>
          <p className="text-3xl font-bold">{summary.profit_margin}%</p>
        </div>
      </div>

      {/* Revenue & Loss Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-4">Revenue vs Loss Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis />
            <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
            <Legend />
            <Bar dataKey="revenue" fill="#10B981" name="Revenue" />
            <Bar dataKey="loss" fill="#EF4444" name="Loss" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" /> Revenue Breakdown
          </h3>
          <p className="text-xs text-gray-500 mb-4">Click on each row to see Fleety vs FleetyGo breakdown</p>
          <div className="space-y-3">
            <RevenueRow 
              label="Platform Fees"
              description="Commission from orders (₹20/order)"
              total={revenue.platform_fees?.total}
              fleety={revenue.platform_fees?.fleety}
              fleetygo={revenue.platform_fees?.fleetygo}
              fleetyOrders={revenue.platform_fees?.fleety_orders}
              fleetygoOrders={revenue.platform_fees?.fleetygo_orders}
              bgColor="bg-green-50"
              textColor="text-green-600"
              sectionKey="platformFees"
            />
            <RevenueRow 
              label={`GST Collected (${revenue.gst?.rate || '18%'})`}
              description="18% of Platform Fees"
              total={revenue.gst?.total}
              fleety={revenue.gst?.fleety}
              fleetygo={revenue.gst?.fleetygo}
              bgColor="bg-purple-50"
              textColor="text-purple-600"
              sectionKey="gst"
            />
            <RevenueRow 
              label="Total Revenue"
              description="Platform Fees + GST"
              total={revenue.total_revenue?.total}
              fleety={revenue.total_revenue?.fleety}
              fleetygo={revenue.total_revenue?.fleetygo}
              bgColor="bg-blue-50"
              textColor="text-blue-600"
              sectionKey="totalRevenue"
            />
          </div>
        </div>

        {/* Losses Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" /> Fleety&apos;s Losses
          </h3>
          <p className="text-xs text-gray-500 mb-4">Platform fees & GST lost due to disputes/cancellations</p>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-gray-900">Dispute Losses</p>
                <p className="text-xl font-bold text-red-600">₹{((losses.dispute_fees_lost || 0) + (losses.dispute_gst_lost || 0)).toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white/50 rounded p-2">
                  <p className="text-gray-600">Platform Fees Lost</p>
                  <p className="font-bold text-red-700">₹{losses.dispute_fees_lost?.toLocaleString()}</p>
                </div>
                <div className="bg-white/50 rounded p-2">
                  <p className="text-gray-600">GST Lost</p>
                  <p className="font-bold text-red-700">₹{losses.dispute_gst_lost?.toLocaleString()}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{losses.dispute_count || 0} disputes resolved</p>
            </div>
            
            <div className="p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-gray-900">Cancelled Order Losses</p>
                <p className="text-xl font-bold text-orange-600">₹{((losses.cancelled_fees_lost || 0) + (losses.cancelled_gst_lost || 0)).toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white/50 rounded p-2">
                  <p className="text-gray-600">Platform Fees Lost</p>
                  <p className="font-bold text-orange-700">₹{losses.cancelled_fees_lost?.toLocaleString()}</p>
                </div>
                <div className="bg-white/50 rounded p-2">
                  <p className="text-gray-600">GST Lost</p>
                  <p className="font-bold text-orange-700">₹{losses.cancelled_gst_lost?.toLocaleString()}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{losses.cancelled_orders || 0} orders cancelled</p>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between">
                <p className="font-semibold text-gray-900">Total Fleety Loss</p>
                <p className="text-xl font-bold text-red-600">₹{losses.total_losses?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Customer Refunds (Restaurant's Loss) */}
          {losses.customer_refunds_total > 0 && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">📋 Customer Refunds (Restaurant&apos;s Burden)</h4>
              <p className="text-2xl font-bold text-gray-600">₹{losses.customer_refunds_total?.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Total amount refunded to customers (paid by restaurants)</p>
            </div>
          )}

          {/* Loss Categories */}
          {losses.by_category && Object.keys(losses.by_category).length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <h4 className="font-medium text-gray-900 mb-3">Issues Breakdown</h4>
              <div className="space-y-2">
                {Object.entries(losses.by_category).map(([category, catData]) => (
                  <div key={category} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">⚠️</span>
                      <span className="text-sm text-gray-700">{category}</span>
                      <span className="text-xs text-gray-400">({catData.count} cases)</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-red-600">₹{((catData.fleety_loss || 0) + (catData.gst_loss || 0)).toLocaleString()} lost</span>
                      <span className="text-xs text-gray-400 block">₹{catData.customer_refund?.toLocaleString()} refunded</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">💡 Insights</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              {losses.by_category && Object.entries(losses.by_category).length > 0 && (
                <li>• Top issue: {Object.entries(losses.by_category).sort((a, b) => (b[1].fleety_loss + b[1].gst_loss) - (a[1].fleety_loss + a[1].gst_loss))[0]?.[0]}</li>
              )}
              {losses.cancelled_orders > 0 && (
                <li>• {losses.cancelled_orders} cancelled orders = ₹{((losses.cancelled_fees_lost || 0) + (losses.cancelled_gst_lost || 0)).toLocaleString()} in lost revenue</li>
              )}
              {summary.profit_margin < 80 && summary.profit_margin > 0 && (
                <li>• Profit margin {summary.profit_margin}% - review dispute handling</li>
              )}
              {summary.profit_margin < 0 && (
                <li>• ⚠️ Negative margin - losses exceeding revenue this period</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notifications Page - Restaurant Approval Workflow
const Notifications = ({ api, onApproval }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [approving, setApproving] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/notifications?_t=${Date.now()}`);
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleApproval = async (restaurantId, status, reason = '') => {
    setApproving(true);
    try {
      const res = await api.put(`/admin/restaurants/${restaurantId}/approve`, {
        approval_status: status,
        rejection_reason: reason
      });
      if (res.data.success) {
        alert(`Restaurant ${status === 'approved' ? 'approved' : status === 'on_hold' ? 'put on hold' : 'removed'} successfully!`);
        fetchNotifications();
        onApproval();
      } else {
        throw new Error(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Approval error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`⚠️ Error updating restaurant status:\n\n${errorMsg}\n\nNote: The database may need schema updates. Please add 'approval_status' column to the restaurants table in Supabase.`);
    } finally {
      setApproving(false);
      setSelectedRestaurant(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
          <p className="text-sm text-gray-500 mt-1">Review and approve new restaurant signups</p>
        </div>
        <button onClick={fetchNotifications} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">
              {notifications.length} restaurant{notifications.length !== 1 ? 's' : ''} pending approval
            </p>
            <p className="text-sm text-yellow-700">Review and approve restaurants before they appear to customers</p>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">All caught up!</p>
          <p className="text-sm text-gray-400 mt-1">No pending restaurant approvals</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {notifications.map((notif) => (
            <div key={notif.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Store className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{notif.restaurant_name}</h3>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                      Pending Approval
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{notif.cuisine}</p>
                  <p className="text-sm text-gray-400 mt-1 truncate">{notif.address}</p>
                  <p className="text-xs text-gray-400 mt-2">
                    Signed up: {formatIST(notif.created_at)}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleApproval(notif.restaurant_id, 'approved')}
                    disabled={approving}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() => setSelectedRestaurant(notif)}
                    className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Review Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedRestaurant && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRestaurant(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Review Restaurant</h3>
                <button onClick={() => setSelectedRestaurant(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Restaurant Name</p>
                <p className="font-semibold text-gray-900">{selectedRestaurant.restaurant_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cuisine</p>
                <p className="text-gray-900">{selectedRestaurant.cuisine}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="text-gray-900">{selectedRestaurant.address}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Signup Date</p>
                <p className="text-gray-900">{formatIST(selectedRestaurant.created_at)}</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => handleApproval(selectedRestaurant.restaurant_id, 'approved')}
                disabled={approving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                ✓ Approve
              </button>
              <button
                onClick={() => handleApproval(selectedRestaurant.restaurant_id, 'on_hold', 'Under review')}
                disabled={approving}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                ⏸ Put On Hold
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove this restaurant? Customers will see "Please contact Fleety customer support".')) {
                    handleApproval(selectedRestaurant.restaurant_id, 'removed', 'Restaurant removed');
                  }
                }}
                disabled={approving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                ✕ Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Platform Control - Toggle Apps Online/Offline
const PlatformControl = ({ api }) => {
  const [settings, setSettings] = useState({
    restaurant_app_enabled: true,
    customer_app_enabled: true,
    rider_app_enabled: true,
    maintenance_mode: false,
    maintenance_message: 'Service temporarily unavailable. Please try again later.',
    // Fleety (Food Delivery)
    orders_paused: false,
    order_pause_message: 'Thank you for your overwhelming response! We are currently not accepting orders. We will be back soon!',
    order_resume_at: null,
    // FleetyGo (Package Delivery)
    fleetygo_paused: false,
    fleetygo_pause_message: 'FleetyGo is currently not accepting deliveries. We will be back soon!',
    fleetygo_resume_at: null,
    // Centralized customer pricing
    packing_charge_enabled: false,
    packing_charge_amount: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dbError, setDbError] = useState(null);
  // Fleety (Food) pause state
  const [pauseMessage, setPauseMessage] = useState('');
  const [resumeTime, setResumeTime] = useState('');
  // FleetyGo (Package) pause state
  const [fleetygoMessage, setFleetygoMessage] = useState('');
  const [fleetygoResumeTime, setFleetygoResumeTime] = useState('');
  const [packingChargeAmount, setPackingChargeAmount] = useState('0');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/platform-settings');
      const loadedSettings = res.data.settings || {};
      setSettings(prev => ({ ...prev, ...loadedSettings }));
      setPackingChargeAmount(String(loadedSettings.packing_charge_amount ?? 0));
      // Fleety
      setPauseMessage(loadedSettings.order_pause_message || 'Thank you for your overwhelming response! We are currently not accepting orders. We will be back soon!');
      setResumeTime(loadedSettings.order_resume_at || '');
      // FleetyGo
      setFleetygoMessage(loadedSettings.fleetygo_pause_message || 'FleetyGo is currently not accepting deliveries. We will be back soon!');
      setFleetygoResumeTime(loadedSettings.fleetygo_resume_at || '');
      setDbError(null);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async (field, value) => {
    setSaving(true);
    try {
      const res = await api.put('/admin/platform-settings', { [field]: value });
      if (res.data.success === false) {
        setDbError(res.data);
        alert('⚠️ Database table not found. Please create the platform_settings table in Supabase.');
      } else {
        setSettings(prev => ({ ...prev, [field]: value }));
        setDbError(null);
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      alert('Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const savePackingChargeSettings = async (enabled, amountValue) => {
    const amount = Number(amountValue);
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Packing Charge Amount must be a valid number greater than or equal to ₹0');
      return;
    }

    setSaving(true);
    try {
      const res = await api.put('/admin/platform-settings', {
        packing_charge_enabled: enabled,
        packing_charge_amount: amount
      });
      if (res.data.success === false) {
        setDbError(res.data);
        alert('⚠️ Database needs to be updated. Please run the SQL shown below.');
      } else {
        const savedSettings = res.data.settings || {};
        setSettings(prev => ({
          ...prev,
          packing_charge_enabled: savedSettings.packing_charge_enabled ?? enabled,
          packing_charge_amount: savedSettings.packing_charge_amount ?? amount
        }));
        setPackingChargeAmount(String(savedSettings.packing_charge_amount ?? amount));
        setDbError(null);
      }
    } catch (err) {
      console.error('Error updating packing charge settings:', err);
      alert(err.response?.data?.detail || 'Error saving packing charge settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePackingChargeToggle = () => {
    savePackingChargeSettings(!settings.packing_charge_enabled, packingChargeAmount);
  };

  const handleSavePackingCharge = () => {
    savePackingChargeSettings(Boolean(settings.packing_charge_enabled), packingChargeAmount);
  };

  const handleOrderPauseToggle = async (pauseOrders) => {
    setSaving(true);
    try {
      const updateData = {
        orders_paused: pauseOrders,
        order_pause_message: pauseMessage,
        order_resume_at: pauseOrders && resumeTime ? resumeTime : null
      };
      
      const res = await api.put('/admin/platform-settings', updateData);
      if (res.data.success === false) {
        setDbError(res.data);
        alert('⚠️ Database needs to be updated. Please run the SQL shown below.');
      } else {
        setSettings(prev => ({ ...prev, ...updateData }));
        setDbError(null);
      }
    } catch (err) {
      console.error('Error updating order pause settings:', err);
      alert('Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePauseMessage = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/platform-settings', { 
        order_pause_message: pauseMessage,
        order_resume_at: resumeTime || null
      });
      if (res.data.success) {
        setSettings(prev => ({ ...prev, order_pause_message: pauseMessage, order_resume_at: resumeTime || null }));
        alert('✅ Fleety settings saved!');
      }
    } catch (err) {
      console.error('Error saving pause message:', err);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  // FleetyGo handlers
  const handleFleetyGoPauseToggle = async (pauseOrders) => {
    setSaving(true);
    try {
      const updateData = {
        fleetygo_paused: pauseOrders,
        fleetygo_pause_message: fleetygoMessage,
        fleetygo_resume_at: pauseOrders && fleetygoResumeTime ? fleetygoResumeTime : null
      };
      
      const res = await api.put('/admin/platform-settings', updateData);
      if (res.data.success === false) {
        setDbError(res.data);
        alert('⚠️ Database needs to be updated. Please run the SQL shown below.');
      } else {
        setSettings(prev => ({ ...prev, ...updateData }));
        setDbError(null);
      }
    } catch (err) {
      console.error('Error updating FleetyGo pause settings:', err);
      alert('Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFleetyGoMessage = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/platform-settings', { 
        fleetygo_pause_message: fleetygoMessage,
        fleetygo_resume_at: fleetygoResumeTime || null
      });
      if (res.data.success) {
        setSettings(prev => ({ ...prev, fleetygo_pause_message: fleetygoMessage, fleetygo_resume_at: fleetygoResumeTime || null }));
        alert('✅ FleetyGo settings saved!');
      }
    } catch (err) {
      console.error('Error saving FleetyGo pause message:', err);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const AppToggleCard = ({ title, subtitle, icon: Icon, enabled, field, color, url }) => (
    <div className={`bg-white rounded-xl p-6 shadow-sm border-2 ${enabled ? 'border-green-200' : 'border-red-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl ${enabled ? `bg-${color}-100` : 'bg-gray-100'} flex items-center justify-center`}>
            <Icon className={`w-7 h-7 ${enabled ? `text-${color}-600` : 'text-gray-400'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block">
                {url}
              </a>
            )}
          </div>
        </div>
        <button
          onClick={() => handleToggle(field, !enabled)}
          disabled={saving}
          className={`relative w-16 h-8 rounded-full transition-colors ${enabled ? 'bg-green-500' : 'bg-gray-300'} ${saving ? 'opacity-50' : ''}`}
        >
          <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${enabled ? 'left-9' : 'left-1'}`} />
        </button>
      </div>
      <div className={`mt-4 flex items-center gap-2 ${enabled ? 'text-green-600' : 'text-red-600'}`}>
        <Power className="w-4 h-4" />
        <span className="text-sm font-medium">{enabled ? 'Online' : 'Offline'}</span>
      </div>
    </div>
  );

  // Show loading spinner while fetching initial settings
  if (loading) {
    return (
      <div className="space-y-6" data-testid="platform-control">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Control</h2>
            <p className="text-sm text-gray-500 mt-1">Enable or disable apps across the Fleety platform</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#3FA9F5] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-control">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Platform Control</h2>
          <p className="text-sm text-gray-500 mt-1">Enable or disable apps across the Fleety platform</p>
        </div>
        <button 
          onClick={fetchSettings} 
          disabled={saving}
          className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]"
        >
          <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Database Setup Warning */}
      {dbError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Database Setup Required</p>
              <p className="text-sm text-yellow-700 mt-1">
                The platform_settings table needs to be created or updated. Run this SQL in Supabase:
              </p>
              <pre className="mt-2 p-3 bg-yellow-100 rounded-lg text-xs text-yellow-900 overflow-x-auto">
{`CREATE TABLE IF NOT EXISTS platform_settings (
    id VARCHAR PRIMARY KEY DEFAULT 'main',
    restaurant_app_enabled BOOLEAN DEFAULT true,
    customer_app_enabled BOOLEAN DEFAULT true,
    rider_app_enabled BOOLEAN DEFAULT true,
    maintenance_mode BOOLEAN DEFAULT false,
    maintenance_message TEXT,
    orders_paused BOOLEAN DEFAULT false,
    order_pause_message TEXT DEFAULT 'Thank you for your overwhelming response! We are currently not accepting orders. We will be back soon!',
    order_resume_at TIMESTAMP,
    fleetygo_paused BOOLEAN DEFAULT false,
    fleetygo_pause_message TEXT DEFAULT 'FleetyGo is currently not accepting deliveries. We will be back soon!',
    fleetygo_resume_at TIMESTAMP,
    packing_charge_enabled BOOLEAN NOT NULL DEFAULT false,
    packing_charge_amount NUMERIC(10,2) NOT NULL DEFAULT 0 CHECK (packing_charge_amount >= 0),
    updated_at TIMESTAMP,
    updated_by VARCHAR
);
INSERT INTO platform_settings (id) VALUES ('main')
ON CONFLICT (id) DO NOTHING;

-- If table exists but missing new columns:
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS orders_paused BOOLEAN DEFAULT false;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS order_pause_message TEXT;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS order_resume_at TIMESTAMP;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS fleetygo_paused BOOLEAN DEFAULT false;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS fleetygo_pause_message TEXT;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS fleetygo_resume_at TIMESTAMP;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS packing_charge_enabled BOOLEAN DEFAULT false;
ALTER TABLE platform_settings ADD COLUMN IF NOT EXISTS packing_charge_amount NUMERIC(10,2) DEFAULT 0;
UPDATE platform_settings SET packing_charge_enabled = COALESCE(packing_charge_enabled, false), packing_charge_amount = CASE WHEN packing_charge_amount IS NULL OR packing_charge_amount < 0 THEN 0 ELSE ROUND(packing_charge_amount, 2) END;
ALTER TABLE platform_settings ALTER COLUMN packing_charge_enabled SET DEFAULT false;
ALTER TABLE platform_settings ALTER COLUMN packing_charge_amount SET DEFAULT 0;`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Centralized Packing Charges */}
      <div className={`rounded-xl p-6 shadow-sm border-2 ${settings.packing_charge_enabled ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${settings.packing_charge_enabled ? 'bg-blue-100' : 'bg-gray-100'} flex items-center justify-center`}>
              <Receipt className={`w-7 h-7 ${settings.packing_charge_enabled ? 'text-blue-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Packing Charges</h3>
              <p className="text-sm text-gray-500">Centralized charge applied by the Customer app at checkout</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${settings.packing_charge_enabled ? 'text-blue-600' : 'text-gray-500'}`}>
              {settings.packing_charge_enabled ? '✅ ON' : 'OFF'}
            </span>
            <button
              onClick={handlePackingChargeToggle}
              disabled={saving}
              aria-label={`${settings.packing_charge_enabled ? 'Disable' : 'Enable'} packing charges`}
              className={`relative w-16 h-8 rounded-full transition-colors ${settings.packing_charge_enabled ? 'bg-blue-500' : 'bg-gray-300'} ${saving ? 'opacity-50' : ''}`}
            >
              <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${settings.packing_charge_enabled ? 'left-9' : 'left-1'}`} />
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="packing-charge-amount">
                Packing Charge Amount (₹)
              </label>
              <input
                id="packing-charge-amount"
                type="number"
                min="0"
                step="0.01"
                value={packingChargeAmount}
                onChange={(e) => setPackingChargeAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                The configured amount is retained while OFF; the effective customer charge remains ₹0 until enabled.
              </p>
            </div>
            <button
              onClick={handleSavePackingCharge}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Packing Charge'}
            </button>
          </div>
          <div className={`mt-4 p-3 rounded-lg ${settings.packing_charge_enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <p className={`text-sm font-medium ${settings.packing_charge_enabled ? 'text-blue-700' : 'text-gray-700'}`}>
              Effective customer packing charge: ₹{settings.packing_charge_enabled ? Number(packingChargeAmount || 0).toFixed(2) : '0.00'}
            </p>
            <p className="text-xs text-gray-600 mt-1">Delivery Fee, Platform Fee, and 5% GST on Food + Packing are unchanged.</p>
          </div>
        </div>
      </div>

      {/* FLEETY (Food Delivery) Order Pause Mode */}
      <div className={`rounded-xl p-6 shadow-sm border-2 ${settings.orders_paused ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${settings.orders_paused ? 'bg-orange-100' : 'bg-gray-100'} flex items-center justify-center`}>
              <ShoppingCart className={`w-7 h-7 ${settings.orders_paused ? 'text-orange-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">🍔 Fleety (Food Delivery)</h3>
              <p className="text-sm text-gray-500">Pause food orders while keeping the platform running</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${settings.orders_paused ? 'text-orange-600' : 'text-green-600'}`}>
              {settings.orders_paused ? '⏸️ PAUSED' : '✅ ACCEPTING'}
            </span>
            <button
              onClick={() => handleOrderPauseToggle(!settings.orders_paused)}
              disabled={saving}
              className={`relative w-16 h-8 rounded-full transition-colors ${settings.orders_paused ? 'bg-orange-500' : 'bg-green-500'} ${saving ? 'opacity-50' : ''}`}
            >
              <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${settings.orders_paused ? 'left-1' : 'left-9'}`} />
            </button>
          </div>
        </div>
        
        {/* Custom Message */}
        <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Message (shown when food orders are paused)
            </label>
            <textarea
              value={pauseMessage}
              onChange={(e) => setPauseMessage(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Thank you for your overwhelming response..."
            />
          </div>
          
          {/* Scheduled Resume */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Auto-Resume (optional)
              </label>
              <input
                type="datetime-local"
                value={resumeTime ? resumeTime.slice(0, 16) : ''}
                onChange={(e) => setResumeTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">Food orders will automatically resume at this time</p>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSavePauseMessage}
                disabled={saving}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
        
        {settings.orders_paused && (
          <div className="mt-4 p-3 bg-orange-100 rounded-lg">
            <p className="text-sm text-orange-700 font-medium">⚠️ Fleety food orders are currently paused!</p>
            <p className="text-xs text-orange-600 mt-1">Customers can browse but cannot place food orders.</p>
            {settings.order_resume_at && (
              <p className="text-xs text-orange-600 mt-1">
                📅 Scheduled to resume: {new Date(settings.order_resume_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* FLEETYGO (Package Delivery) Order Pause Mode */}
      <div className={`rounded-xl p-6 shadow-sm border-2 ${settings.fleetygo_paused ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${settings.fleetygo_paused ? 'bg-purple-100' : 'bg-gray-100'} flex items-center justify-center`}>
              <Package className={`w-7 h-7 ${settings.fleetygo_paused ? 'text-purple-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">📦 FleetyGo (Package Delivery)</h3>
              <p className="text-sm text-gray-500">Pause package deliveries while keeping the platform running</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${settings.fleetygo_paused ? 'text-purple-600' : 'text-green-600'}`}>
              {settings.fleetygo_paused ? '⏸️ PAUSED' : '✅ ACCEPTING'}
            </span>
            <button
              onClick={() => handleFleetyGoPauseToggle(!settings.fleetygo_paused)}
              disabled={saving}
              className={`relative w-16 h-8 rounded-full transition-colors ${settings.fleetygo_paused ? 'bg-purple-500' : 'bg-green-500'} ${saving ? 'opacity-50' : ''}`}
            >
              <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${settings.fleetygo_paused ? 'left-1' : 'left-9'}`} />
            </button>
          </div>
        </div>
        
        {/* Custom Message */}
        <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Message (shown when package deliveries are paused)
            </label>
            <textarea
              value={fleetygoMessage}
              onChange={(e) => setFleetygoMessage(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="FleetyGo is currently not accepting deliveries..."
            />
          </div>
          
          {/* Scheduled Resume */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Auto-Resume (optional)
              </label>
              <input
                type="datetime-local"
                value={fleetygoResumeTime ? fleetygoResumeTime.slice(0, 16) : ''}
                onChange={(e) => setFleetygoResumeTime(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Package deliveries will automatically resume at this time</p>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSaveFleetyGoMessage}
                disabled={saving}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
        
        {settings.fleetygo_paused && (
          <div className="mt-4 p-3 bg-purple-100 rounded-lg">
            <p className="text-sm text-purple-700 font-medium">⚠️ FleetyGo package deliveries are currently paused!</p>
            <p className="text-xs text-purple-600 mt-1">Customers can browse but cannot request package deliveries.</p>
            {settings.fleetygo_resume_at && (
              <p className="text-xs text-purple-600 mt-1">
                📅 Scheduled to resume: {new Date(settings.fleetygo_resume_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Maintenance Mode */}
      <div className={`rounded-xl p-6 shadow-sm border-2 ${settings.maintenance_mode ? 'bg-red-50 border-red-300' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${settings.maintenance_mode ? 'bg-red-100' : 'bg-gray-100'} flex items-center justify-center`}>
              <Globe className={`w-7 h-7 ${settings.maintenance_mode ? 'text-red-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Maintenance Mode</h3>
              <p className="text-sm text-gray-500">When enabled, all apps show maintenance message</p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('maintenance_mode', !settings.maintenance_mode)}
            disabled={saving}
            className={`relative w-16 h-8 rounded-full transition-colors ${settings.maintenance_mode ? 'bg-red-500' : 'bg-gray-300'} ${saving ? 'opacity-50' : ''}`}
          >
            <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${settings.maintenance_mode ? 'left-9' : 'left-1'}`} />
          </button>
        </div>
        {settings.maintenance_mode && (
          <div className="mt-4 p-3 bg-red-100 rounded-lg">
            <p className="text-sm text-red-700 font-medium">⚠️ Platform is in maintenance mode!</p>
          </div>
        )}
      </div>

      {/* App Toggles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AppToggleCard
          title="Restaurant App"
          subtitle="Partner/Menu Management"
          icon={Store}
          enabled={settings.restaurant_app_enabled}
          field="restaurant_app_enabled"
          color="green"
          url={window.location.origin}
        />
        <AppToggleCard
          title="Customer App"
          subtitle="Food Ordering"
          icon={Smartphone}
          enabled={settings.customer_app_enabled}
          field="customer_app_enabled"
          color="blue"
          url={window.location.origin}
        />
        <AppToggleCard
          title="Rider App"
          subtitle="Delivery Management"
          icon={Bike}
          enabled={settings.rider_app_enabled}
          field="rider_app_enabled"
          color="purple"
          url={window.location.origin}
        />
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Note:</p>
            <p className="mt-1">
              These toggles update the <code className="bg-blue-100 px-1 rounded">platform_settings</code> table in Supabase. 
              The individual apps need to be configured to read this table and show appropriate messages when disabled.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Support Tickets Management
const SupportTickets = ({ api }) => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [filter, setFilter] = useState({ status: '', priority: '', type: '' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const ticketTypeLabels = {
    payout_mismatch: 'Payout Mismatch',
    refund_dispute: 'Refund Dispute',
    order_issue: 'Order Issue',
    auto_rejection: 'Auto Rejection',
    account_issue: 'Account Issue',
    rating_dispute: 'Rating Dispute',
    other: 'Other'
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      if (filter.type) params.append('ticket_type', filter.type);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 15);
      params.append('_t', Date.now());
      
      const res = await api.get(`/admin/support-tickets?${params.toString()}`);
      setTickets(res.data.tickets || []);
      setStats(res.data.stats || {});
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, search, page]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/admin/support-tickets/${ticketId}`);
      setTicketDetail(res.data.ticket);
    } catch (err) {
      console.error('Error fetching ticket detail:', err);
    }
  };

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setResolutionNotes(ticket.resolution_notes || '');
    await fetchTicketDetail(ticket.id);
  };

  const handleUpdateTicket = async (ticketId, updates) => {
    setUpdating(true);
    try {
      await api.put(`/admin/support-tickets/${ticketId}`, updates);
      fetchTickets();
      if (ticketDetail) {
        await fetchTicketDetail(ticketId);
      }
      if (updates.status === 'resolved' || updates.status === 'rejected') {
        setSelectedTicket(null);
        setTicketDetail(null);
      }
    } catch (err) {
      alert('Error updating ticket');
    } finally {
      setUpdating(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_review': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'payout_mismatch': return '💰';
      case 'refund_dispute': return '🔄';
      case 'order_issue': return '📦';
      case 'auto_rejection': return '🚫';
      case 'account_issue': return '👤';
      case 'rating_dispute': return '⭐';
      default: return '📋';
    }
  };

  return (
    <div className="space-y-6" data-testid="support-tickets">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
          <p className="text-sm text-gray-500 mt-1">Manage restaurant partner support requests</p>
        </div>
        <button onClick={fetchTickets} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
          <p className="text-sm text-gray-500">Total Tickets</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-100">
          <p className="text-2xl font-bold text-blue-600">{stats.open || 0}</p>
          <p className="text-sm text-blue-600">Open</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-100">
          <p className="text-2xl font-bold text-yellow-600">{stats.in_review || 0}</p>
          <p className="text-sm text-yellow-600">In Review</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-100">
          <p className="text-2xl font-bold text-green-600">{stats.resolved || 0}</p>
          <p className="text-sm text-green-600">Resolved</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 shadow-sm border border-red-100">
          <p className="text-2xl font-bold text-red-600">{stats.urgent || 0}</p>
          <p className="text-sm text-red-600">Urgent</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 shadow-sm border border-orange-100">
          <p className="text-2xl font-bold text-orange-600">{stats.high || 0}</p>
          <p className="text-sm text-orange-600">High Priority</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ticket #, subject, restaurant..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <select
            value={filter.status}
            onChange={(e) => { setFilter({ ...filter, status: e.target.value }); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_review">In Review</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={filter.priority}
            onChange={(e) => { setFilter({ ...filter, priority: e.target.value }); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Priority</option>
            <option value="urgent">🔴 Urgent</option>
            <option value="high">🟠 High</option>
            <option value="normal">⚪ Normal</option>
          </select>
          <select
            value={filter.type}
            onChange={(e) => { setFilter({ ...filter, type: e.target.value }); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="payout_mismatch">💰 Payout Mismatch</option>
            <option value="refund_dispute">🔄 Refund Dispute</option>
            <option value="order_issue">📦 Order Issue</option>
            <option value="auto_rejection">🚫 Auto Rejection</option>
            <option value="account_issue">👤 Account Issue</option>
            <option value="rating_dispute">⭐ Rating Dispute</option>
            <option value="other">📋 Other</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      {loading ? (
        <LoadingSpinner />
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No support tickets found</p>
          <p className="text-sm text-gray-400 mt-1">Tickets from restaurant partners will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => handleOpenTicket(ticket)}
              className={`bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:shadow-md transition-shadow ${
                ticket.priority === 'urgent' ? 'border-red-200' : 
                ticket.priority === 'high' ? 'border-orange-200' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="text-2xl">{getTypeIcon(ticket.ticket_type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-gray-500">{ticket.ticket_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority?.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mt-1">{ticket.subject}</h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{ticket.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Store className="w-3 h-3" /> {ticket.restaurant_name}
                    </span>
                    <span>{ticketTypeLabels[ticket.ticket_type] || ticket.ticket_type}</span>
                    <span>{formatIST(ticket.created_at)}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-4 py-2 text-sm">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border rounded-lg disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setSelectedTicket(null); setTicketDetail(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-500">{selectedTicket.ticket_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticketDetail?.status || selectedTicket.status)}`}>
                      {(ticketDetail?.status || selectedTicket.status)?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(ticketDetail?.priority || selectedTicket.priority)}`}>
                      {(ticketDetail?.priority || selectedTicket.priority)?.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{selectedTicket.subject}</h3>
                </div>
                <button onClick={() => { setSelectedTicket(null); setTicketDetail(null); }} className="p-2 hover:bg-gray-200 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Ticket Info */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-medium flex items-center gap-2">
                      {getTypeIcon(selectedTicket.ticket_type)} {ticketTypeLabels[selectedTicket.ticket_type] || selectedTicket.ticket_type}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Restaurant</p>
                    <p className="font-medium">{ticketDetail?.restaurant_name || selectedTicket.restaurant_name}</p>
                  </div>
                  {selectedTicket.order_id && (
                    <div>
                      <p className="text-sm text-gray-500">Related Order</p>
                      <p className="font-mono text-sm">{selectedTicket.order_id}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Submitted</p>
                    <p className="text-sm">{formatIST(selectedTicket.created_at)}</p>
                  </div>
                  {ticketDetail?.resolved_at && (
                    <div>
                      <p className="text-sm text-gray-500">Resolved</p>
                      <p className="text-sm">{formatIST(ticketDetail.resolved_at)} by {ticketDetail.resolved_by}</p>
                    </div>
                  )}
                </div>

                {/* Right Column - Description */}
                <div>
                  <p className="text-sm text-gray-500 mb-2">Description</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTicket.description}
                  </div>
                </div>
              </div>

              {/* Evidence Images */}
              {(ticketDetail?.evidence_urls?.length > 0 || selectedTicket.evidence_urls?.length > 0) && (
                <div className="mt-6">
                  <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Evidence ({(ticketDetail?.evidence_urls || selectedTicket.evidence_urls).length} images)
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {(ticketDetail?.evidence_urls || selectedTicket.evidence_urls).map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80">
                        <img src={url} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolution Notes */}
              {(ticketDetail?.status === 'open' || ticketDetail?.status === 'in_review') && (
                <div className="mt-6">
                  <p className="text-sm text-gray-500 mb-2">Resolution Notes</p>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Add notes about the resolution..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm resize-none"
                    rows={3}
                  />
                </div>
              )}

              {ticketDetail?.resolution_notes && ticketDetail?.status !== 'open' && (
                <div className="mt-6">
                  <p className="text-sm text-gray-500 mb-2">Resolution Notes</p>
                  <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800">
                    {ticketDetail.resolution_notes}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            {(ticketDetail?.status === 'open' || ticketDetail?.status === 'in_review') && (
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  {ticketDetail?.status === 'open' && (
                    <button
                      onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'in_review' })}
                      disabled={updating}
                      className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                    >
                      📋 Mark In Review
                    </button>
                  )}
                  <button
                    onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'resolved', resolution_notes: resolutionNotes })}
                    disabled={updating}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    ✓ Resolve Ticket
                  </button>
                  <button
                    onClick={() => handleUpdateTicket(selectedTicket.id, { status: 'rejected', resolution_notes: resolutionNotes })}
                    disabled={updating}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    ✕ Reject
                  </button>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleUpdateTicket(selectedTicket.id, { priority: 'urgent' })}
                    disabled={updating || ticketDetail?.priority === 'urgent'}
                    className="px-3 py-1.5 border border-red-200 text-red-600 rounded text-sm hover:bg-red-50 disabled:opacity-50"
                  >
                    🔴 Set Urgent
                  </button>
                  <button
                    onClick={() => handleUpdateTicket(selectedTicket.id, { priority: 'high' })}
                    disabled={updating || ticketDetail?.priority === 'high'}
                    className="px-3 py-1.5 border border-orange-200 text-orange-600 rounded text-sm hover:bg-orange-50 disabled:opacity-50"
                  >
                    🟠 Set High
                  </button>
                  <button
                    onClick={() => handleUpdateTicket(selectedTicket.id, { priority: 'normal' })}
                    disabled={updating || ticketDetail?.priority === 'normal'}
                    className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    ⚪ Set Normal
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Ratings Management
const Ratings = ({ api }) => {
  const [viewMode, setViewMode] = useState('orderwise'); // 'orderwise' or 'cumulative'
  const [ratings, setRatings] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [minRating, setMinRating] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tableMissing, setTableMissing] = useState(false);
  const [setupSql, setSetupSql] = useState("");
  
  // Cumulative view state
  const [entities, setEntities] = useState([]);
  const [entityFilter, setEntityFilter] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityRatings, setEntityRatings] = useState([]);
  const [loadingEntityRatings, setLoadingEntityRatings] = useState(false);

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter) params.append("rating_type", filter);
      if (minRating) params.append("min_rating", minRating);
      if (orderSearch) params.append("order_id", orderSearch);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("_t", Date.now());
      const res = await api.get(`/admin/ratings?${params.toString()}`);
      setRatings(res.data.ratings);
      setTotalPages(res.data.total_pages);
      if (res.data.table_missing) {
        setTableMissing(true);
        setSetupSql(res.data.setup_sql);
      } else {
        setTableMissing(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, minRating, orderSearch, startDate, endDate, page]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/admin/ratings/summary');
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  const fetchCumulativeRatings = useCallback(async () => {
    setLoading(true);
    try {
      // Try stats/entities path
      let url = '/admin/stats/entities';
      if (entityFilter) {
        url += `?entity_type=${entityFilter}`;
      }
      const res = await api.get(url);
      setEntities(res?.data?.entities || []);
    } catch (err) {
      console.error('Error fetching cumulative ratings:', err);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [api, entityFilter]);

  const fetchEntityRatings = useCallback(async (entityId) => {
    setLoadingEntityRatings(true);
    try {
      const res = await api.get(`/admin/ratings/entity/${entityId}?limit=10`);
      setEntityRatings(res.data.ratings || []);
    } catch (err) {
      console.error(err);
      setEntityRatings([]);
    } finally {
      setLoadingEntityRatings(false);
    }
  }, [api]);

  // Initial fetch when component mounts and api is ready
  useEffect(() => {
    if (!api) return;
    fetchRatings();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Refetch when viewMode changes
  useEffect(() => {
    if (!api) return;
    setLoading(true);
    if (viewMode === 'orderwise') {
      fetchRatings();
    } else {
      fetchCumulativeRatings();
    }
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Refetch cumulative ratings when entity filter changes
  useEffect(() => {
    if (viewMode === 'cumulative') {
      fetchCumulativeRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityFilter]);

  // Refetch order-wise ratings when filters change
  useEffect(() => {
    if (viewMode === 'orderwise') {
      fetchRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, minRating, orderSearch, startDate, endDate, page]);

  const handleEntityClick = (entity) => {
    setSelectedEntity(entity);
    fetchEntityRatings(entity.entity_id);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setOrderSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setOrderSearch("");
    setFilter("");
    setMinRating("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  const getRatingTypeLabel = (type) => {
    const labels = {
      'customer_to_rider': 'Customer → Rider',
      'customer_to_restaurant': 'Customer → Restaurant',
      'rider_to_customer': 'Rider → Customer',
      'restaurant_to_rider': 'Restaurant → Rider'
    };
    return labels[type] || type;
  };

  const getRatingTypeBadge = (type) => {
    const styles = {
      'customer_to_rider': 'bg-blue-100 text-blue-800',
      'customer_to_restaurant': 'bg-purple-100 text-purple-800',
      'rider_to_customer': 'bg-green-100 text-green-800',
      'restaurant_to_rider': 'bg-orange-100 text-orange-800'
    };
    return styles[type] || 'bg-gray-100 text-gray-800';
  };

  const getEntityTypeBadge = (type) => {
    const styles = {
      'restaurant': 'bg-purple-100 text-purple-800',
      'rider': 'bg-blue-100 text-blue-800',
      'customer': 'bg-green-100 text-green-800'
    };
    return styles[type] || 'bg-gray-100 text-gray-800';
  };

  const getEntityIcon = (type) => {
    if (type === 'restaurant') return <Store className="w-4 h-4" />;
    if (type === 'rider') return <Bike className="w-4 h-4" />;
    if (type === 'customer') return <Users className="w-4 h-4" />;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="ratings-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Ratings & Reviews</h2>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('orderwise')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${viewMode === 'orderwise' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="orderwise-toggle"
            >
              Order-wise
            </button>
            <button
              onClick={() => setViewMode('cumulative')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${viewMode === 'cumulative' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="cumulative-toggle"
            >
              Cumulative
            </button>
          </div>
          <button onClick={() => { viewMode === 'orderwise' ? fetchRatings() : fetchCumulativeRatings(); fetchSummary(); }} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100 text-center">
          <p className="text-3xl font-bold text-gray-900">{summary.total_ratings || 0}</p>
          <p className="text-sm text-gray-500">Total Ratings</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 text-center">
          <div className="flex items-center justify-center gap-1">
            <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            <p className="text-3xl font-bold text-yellow-600">{summary.average_rating || 0}</p>
          </div>
          <p className="text-sm text-yellow-600">Overall Average</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
          <div className="flex items-center justify-center gap-1">
            <Bike className="w-5 h-5 text-blue-600" />
            <p className="text-3xl font-bold text-blue-600">{summary.rider_average || 0}</p>
          </div>
          <p className="text-sm text-blue-600">Rider Avg ({summary.rider_count || 0})</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
          <div className="flex items-center justify-center gap-1">
            <Store className="w-5 h-5 text-purple-600" />
            <p className="text-3xl font-bold text-purple-600">{summary.restaurant_average || 0}</p>
          </div>
          <p className="text-sm text-purple-600">Restaurant Avg ({summary.restaurant_count || 0})</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
          <div className="flex items-center justify-center gap-1">
            <Users className="w-5 h-5 text-green-600" />
            <p className="text-3xl font-bold text-green-600">{summary.customer_average || 0}</p>
          </div>
          <p className="text-sm text-green-600">Customer Avg ({summary.customer_count || 0})</p>
        </div>
      </div>

      {/* Star Distribution */}
      {summary.by_stars && (
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Rating Distribution</h3>
          <div className="flex items-center gap-4">
            {[5, 4, 3, 2, 1].map((stars) => (
              <div key={stars} className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{stars}★</span>
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 rounded-full"
                    style={{ width: `${summary.total_ratings ? (summary.by_stars[stars] / summary.total_ratings) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{summary.by_stars[stars] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== ORDER-WISE VIEW ========== */}
      {viewMode === 'orderwise' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-4">
            <div className="flex flex-wrap gap-4">
              {/* Order ID Search */}
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by Order ID..."
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5] w-48"
                    data-testid="order-search-input"
                  />
                </div>
                <button type="submit" className="px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
                  Search
                </button>
              </form>

          {/* Rating Type Filter */}
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5]"
          >
            <option value="">All Rating Types</option>
            <option value="customer_to_rider">Customer → Rider</option>
            <option value="customer_to_restaurant">Customer → Restaurant</option>
            <option value="rider_to_customer">Rider → Customer</option>
            <option value="restaurant_to_rider">Restaurant → Rider</option>
          </select>

          {/* Min Rating Filter */}
          <select
            value={minRating}
            onChange={(e) => { setMinRating(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5]"
          >
            <option value="">All Stars</option>
            <option value="5">5 Stars Only</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
            <option value="1">1+ Stars</option>
          </select>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5]"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5]"
            />
          </div>

          {/* Clear Filters */}
          {(filter || minRating || orderSearch || startDate || endDate) && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Missing Warning */}
      {tableMissing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-2">Database Table Not Found</h3>
              <p className="text-sm text-amber-700 mb-4">
                The <code className="bg-amber-100 px-1 rounded">ratings</code> table needs to be created in Supabase.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-xs whitespace-pre">{setupSql}</pre>
              </div>
              <button 
                onClick={() => { fetchRatings(); fetchSummary(); }}
                className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
              >
                Refresh After Creating Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ratings List */}
      {loading ? (
        <LoadingSpinner />
      ) : tableMissing ? null : ratings.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No ratings found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Order ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rater</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Ratee</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rating</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Review</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ratings.map((rating) => (
                  <tr key={rating.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(rating.order_id || '');
                            // Show brief feedback - could use toast here
                          }}
                          className="font-mono text-sm text-[#3FA9F5] hover:text-[#2d8fd4] text-left flex items-center gap-1 group"
                          title={`Click to copy: ${rating.order_id || 'N/A'}`}
                        >
                          <span>{rating.order_id || 'N/A'}</span>
                          <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                        <span className="text-xs text-gray-500 capitalize px-1.5 py-0.5 bg-gray-100 rounded w-fit">{rating.order_type || 'fleety'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingTypeBadge(rating.rating_type)}`}>
                        {getRatingTypeLabel(rating.rating_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-gray-900">{rating.rater_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{rating.rater_type}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-gray-900">{rating.ratee_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{rating.ratee_type}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {renderStars(rating.rating)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600 max-w-[200px] truncate" title={rating.review}>
                        {rating.review || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatIST(rating.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* ========== CUMULATIVE VIEW ========== */}
      {viewMode === 'cumulative' && (
        <>
          {/* Entity Type Filter */}
          <div className="flex gap-2">
            {['', 'restaurant', 'rider', 'customer'].map((type) => (
              <button
                key={type}
                onClick={() => setEntityFilter(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${entityFilter === type ? 'bg-[#3FA9F5] text-white' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
              >
                {type || 'All Entities'}
              </button>
            ))}
          </div>

          {/* Entities List */}
          {loading ? (
            <LoadingSpinner />
          ) : entities.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No rated entities found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Entity</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Average Rating</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Total Ratings</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Last Rated</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {entities.map((entity) => (
                      <tr key={`${entity.entity_type}-${entity.entity_id}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getEntityIcon(entity.entity_type)}
                            <span className="font-medium text-gray-900">{entity.entity_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getEntityTypeBadge(entity.entity_type)}`}>
                            {entity.entity_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {renderStars(Math.round(entity.average_rating))}
                          <span className="ml-2 text-gray-500 text-xs">({entity.average_rating})</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{entity.total_ratings}</td>
                        <td className="px-4 py-3 text-gray-500">{formatIST(entity.last_rated)}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEntityClick(entity)}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                          >
                            View Ratings
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== ENTITY RATINGS SIDE PANEL ========== */}
      {selectedEntity && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelectedEntity(null)}>
          <div 
            className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  {getEntityIcon(selectedEntity.entity_type)}
                  {selectedEntity.entity_name}
                </h3>
                <p className="text-sm text-gray-500 capitalize">{selectedEntity.entity_type} • {selectedEntity.total_ratings} ratings • Avg: {selectedEntity.average_rating}★</p>
              </div>
              <button 
                onClick={() => setSelectedEntity(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Last 10 Ratings</h4>
              
              {loadingEntityRatings ? (
                <LoadingSpinner />
              ) : entityRatings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No ratings found</p>
              ) : (
                <div className="space-y-4">
                  {entityRatings.map((rating) => (
                    <div key={rating.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        {renderStars(rating.rating)}
                        <span className="text-xs text-gray-500">{formatIST(rating.created_at)}</span>
                      </div>
                      {rating.review && (
                        <p className="text-sm text-gray-700 mb-2">"{rating.review}"</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Order: {rating.order_id?.substring(0, 8) || 'N/A'}</span>
                        <span>•</span>
                        <span>By: {rating.rater_name}</span>
                        <span>•</span>
                        <span className="capitalize">{rating.order_type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Account Deletion Requests Management
const AccountDeletionRequests = ({ api }) => {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionModal, setActionModal] = useState(null);
  const [actionNotes, setActionNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tableMissing, setTableMissing] = useState(false);
  const [setupSql, setSetupSql] = useState("");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter) params.append("status", filter);
      if (phoneSearch) params.append("phone_search", phoneSearch);
      params.append("_t", Date.now());
      const res = await api.get(`/admin/deletion-requests?${params.toString()}`);
      setRequests(res.data.requests);
      setSummary(res.data.summary);
      setTotalPages(res.data.total_pages);
      if (res.data.table_missing) {
        setTableMissing(true);
        setSetupSql(res.data.setup_sql);
      } else {
        setTableMissing(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filter, phoneSearch, page]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPhoneSearch(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setPhoneSearch("");
    setPage(1);
  };

  const handleUpdateStatus = async (requestId, newStatus, notes = "") => {
    setProcessing(true);
    try {
      await api.put(`/admin/deletion-requests/${requestId}`, {
        status: newStatus,
        notes: notes || undefined
      });
      setActionModal(null);
      setActionNotes("");
      fetchRequests();
    } catch (err) {
      console.error(err);
      alert("Failed to update status: " + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async (userId, requestId) => {
    setProcessing(true);
    try {
      const res = await api.delete(`/admin/users/${userId}?request_id=${requestId}`);
      alert(res.data.message);
      setDeleteConfirm(null);
      fetchRequests();
    } catch (err) {
      console.error(err);
      alert("Failed to delete user: " + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      approved: "bg-green-100 text-green-800",
      completed: "bg-emerald-100 text-emerald-800",
      rejected: "bg-red-100 text-red-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6" data-testid="deletion-requests-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Account Deletion Requests</h2>
        <button onClick={fetchRequests} className="flex items-center gap-2 px-3 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.total || 0}</p>
          <p className="text-sm text-gray-500">Total Requests</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 text-center cursor-pointer hover:bg-yellow-100" onClick={() => { setFilter(filter === 'pending' ? '' : 'pending'); setPage(1); }}>
          <p className="text-2xl font-bold text-yellow-600">{summary.pending || 0}</p>
          <p className="text-sm text-yellow-600">Pending</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center cursor-pointer hover:bg-blue-100" onClick={() => { setFilter(filter === 'processing' ? '' : 'processing'); setPage(1); }}>
          <p className="text-2xl font-bold text-blue-600">{summary.processing || 0}</p>
          <p className="text-sm text-blue-600">Processing</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center cursor-pointer hover:bg-green-100" onClick={() => { setFilter(filter === 'approved' ? '' : 'approved'); setPage(1); }}>
          <p className="text-2xl font-bold text-green-600">{summary.approved || 0}</p>
          <p className="text-sm text-green-600">Approved</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 text-center cursor-pointer hover:bg-emerald-100" onClick={() => { setFilter(filter === 'completed' ? '' : 'completed'); setPage(1); }}>
          <p className="text-2xl font-bold text-emerald-600">{summary.completed || 0}</p>
          <p className="text-sm text-emerald-600">Completed</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200 text-center cursor-pointer hover:bg-red-100" onClick={() => { setFilter(filter === 'rejected' ? '' : 'rejected'); setPage(1); }}>
          <p className="text-2xl font-bold text-red-600">{summary.rejected || 0}</p>
          <p className="text-sm text-red-600">Rejected</p>
        </div>
      </div>

      {/* Phone Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by phone number..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3FA9F5]"
            data-testid="phone-search-input"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm hover:bg-[#2d8fd4]"
        >
          Search
        </button>
        {phoneSearch && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
          >
            Clear
          </button>
        )}
      </form>
      
      {phoneSearch && (
        <p className="text-sm text-gray-500">Showing results for phone: "{phoneSearch}"</p>
      )}

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {['', 'pending', 'processing', 'approved', 'completed', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => { setFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === status ? 'bg-[#3FA9F5] text-white' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {/* Table Setup Warning */}
      {tableMissing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-2">Database Table Not Found</h3>
              <p className="text-sm text-amber-700 mb-4">
                The <code className="bg-amber-100 px-1 rounded">account_deletion_requests</code> table needs to be created in Supabase. 
                Run the following SQL in your Supabase SQL Editor:
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-xs whitespace-pre">{setupSql || `CREATE TABLE account_deletion_requests (
  id UUID DEFAULT uuid_generate_v4() PRIMARY KEY,
  user_id TEXT NOT NULL,
  phone_number TEXT NOT NULL,
  reason TEXT,
  status TEXT DEFAULT 'pending',
  request_type TEXT DEFAULT 'account_deletion',
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  processed_at TIMESTAMP WITH TIME ZONE,
  processed_by TEXT,
  notes TEXT
);`}</pre>
              </div>
              <button 
                onClick={fetchRequests}
                className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
              >
                Refresh After Creating Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requests List */}
      {loading ? (
        <LoadingSpinner />
      ) : tableMissing ? null : requests.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <UserX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No deletion requests found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">User</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Reason</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Requested</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{req.user_name}</p>
                        <p className="text-xs text-gray-500">{req.user_email || req.user_id?.substring(0, 8) + '...'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{req.phone_number}</td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600 max-w-[200px] truncate" title={req.reason}>
                        {req.reason || 'No reason provided'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatIST(req.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(req.status)}`}>
                        {req.status}
                      </span>
                      {req.processed_by && (
                        <p className="text-xs text-gray-400 mt-1">by {req.processed_by}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(req.id, 'processing')}
                              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              disabled={processing}
                            >
                              Start Processing
                            </button>
                            <button
                              onClick={() => setActionModal({ type: 'reject', request: req })}
                              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {req.status === 'processing' && (
                          <>
                            <button
                              onClick={() => setDeleteConfirm(req)}
                              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" /> Notify & Delete
                            </button>
                            <button
                              onClick={() => setActionModal({ type: 'reject', request: req })}
                              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Approved & Deleted
                          </span>
                        )}
                        {req.status === 'completed' && (
                          <span className="text-emerald-600 text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Completed
                          </span>
                        )}
                        {req.status === 'rejected' && (
                          <span className="text-red-600 text-xs flex items-center gap-1" title={req.notes}>
                            <XCircle className="w-3 h-3" /> {req.notes?.substring(0, 20) || 'Rejected'}...
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {actionModal?.type === 'reject' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reject Deletion Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Rejecting request from <strong>{actionModal.request.user_name}</strong> ({actionModal.request.phone_number})
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for rejection</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-24"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setActionModal(null); setActionNotes(""); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateStatus(actionModal.request.id, 'rejected', actionNotes)}
                disabled={processing || !actionNotes.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Notify User & Delete Account</h3>
                <p className="text-sm text-gray-500">User will be notified via email before deletion</p>
              </div>
            </div>
            
            {/* Email Notification Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                <strong>Email Notification:</strong>
              </p>
              <p className="text-sm text-blue-700 mt-1">
                A confirmation email will be sent to the user's registered email address notifying them that their account has been deleted.
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> After notification, this will permanently delete:
              </p>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                <li>User profile and credentials</li>
                <li>Saved addresses</li>
                <li>Cart items</li>
                <li>Order history will be anonymized (kept for records)</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm"><strong>User:</strong> {deleteConfirm.user_name}</p>
              <p className="text-sm"><strong>Email:</strong> {deleteConfirm.user_email || 'N/A'}</p>
              <p className="text-sm"><strong>Phone:</strong> {deleteConfirm.phone_number}</p>
              <p className="text-sm"><strong>Reason:</strong> {deleteConfirm.reason || 'Not specified'}</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm.user_id, deleteConfirm.id)}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {processing ? 'Sending Email & Deleting...' : 'Notify & Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Live Orders
// Order Monitoring - Real-time order tracking with alerts
const OrderMonitoring = ({ api }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warningCount, setWarningCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [availableRiders, setAvailableRiders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState("all"); // all, pending, critical
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Sound settings
  const [soundEnabled, setSoundEnabled] = useState(true);
  const playedAlertsRef = useRef({});
  
  // Modals
  const [reassignModal, setReassignModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedRider, setSelectedRider] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Audio context for alert sound
  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
      
      // Second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        osc2.connect(gainNode);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        osc2.start();
        osc2.stop(audioContext.currentTime + 0.3);
      }, 400);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, [soundEnabled]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType && filterType !== 'all') params.append("filter_type", filterType);
      if (selectedRestaurant) params.append("restaurant_id", selectedRestaurant);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      params.append("_t", Date.now());
      
      const res = await api.get(`/orders/live?enhanced=true&${params.toString()}`);
      setOrders(res.data.orders || []);
      setWarningCount(res.data.warning_count || 0);
      setCriticalCount(res.data.critical_count || 0);
      setAvailableRiders(res.data.available_riders || []);
      setRestaurants(res.data.restaurants || []);
      setLastUpdated(new Date());
      
      // Play sound for new warning/critical orders (using ref to avoid re-renders)
      const newWarnings = (res.data.orders || []).filter(
        o => (o.alert_level === 'warning' || o.alert_level === 'critical') && !playedAlertsRef.current[o.id]
      );
      
      if (newWarnings.length > 0) {
        playAlertSound();
        newWarnings.forEach(o => { playedAlertsRef.current[o.id] = true; });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filterType, selectedRestaurant, dateFrom, dateTo, playAlertSound]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000); // 30 seconds auto-refresh
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Format elapsed time
  const formatElapsed = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  // Handle reassign
  const handleReassign = async () => {
    if (!reassignModal || !selectedRider) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/orders/${reassignModal.id}/reassign`, {
        rider_id: selectedRider.id,
        rider_name: selectedRider.name,
        rider_phone: selectedRider.phone
      });
      setReassignModal(null);
      setSelectedRider(null);
      fetchOrders();
    } catch (err) {
      console.error(err);
      alert('Failed to reassign order');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    if (!cancelModal || !cancelReason.trim()) return;
    setActionLoading(true);
    try {
      const res = await api.put(`/admin/orders/${cancelModal.id}/cancel`, {
        reason: cancelReason
      });
      setCancelModal(null);
      setCancelReason("");
      if (res.data.needs_refund) {
        alert(res.data.refund_note);
      }
      fetchOrders();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  // Get row background color based on alert level
  const getRowClass = (order) => {
    if (order.alert_level === 'critical') return 'bg-red-50 border-red-200';
    if (order.alert_level === 'warning') return 'bg-yellow-50 border-yellow-200';
    return 'bg-white border-gray-100';
  };

  return (
    <div className="space-y-6" data-testid="order-monitoring-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Radio className="w-7 h-7 text-[#3FA9F5] animate-pulse" />
            Order Monitoring
          </h2>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {getCurrentIST()} IST • Auto-refresh: 30s
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
              soundEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            Sound {soundEnabled ? 'ON' : 'OFF'}
          </button>
          <button 
            onClick={fetchOrders} 
            className="flex items-center gap-2 px-4 py-2 bg-[#3FA9F5] text-white rounded-lg text-sm font-medium hover:bg-[#2d8fd4]"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Now
          </button>
        </div>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Active</p>
              <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <button
          onClick={() => setFilterType(filterType === 'pending' ? 'all' : 'pending')}
          className={`rounded-xl p-4 border shadow-sm text-left transition ${
            filterType === 'pending' ? 'bg-yellow-100 border-yellow-400 ring-2 ring-yellow-400' : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
          }`}
          data-testid="warning-filter"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700">Warning (&gt;5min)</p>
              <p className="text-3xl font-bold text-yellow-600">{warningCount}</p>
            </div>
            <div className={`w-12 h-12 bg-yellow-200 rounded-lg flex items-center justify-center ${warningCount > 0 ? 'animate-pulse' : ''}`}>
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilterType(filterType === 'critical' ? 'all' : 'critical')}
          className={`rounded-xl p-4 border shadow-sm text-left transition ${
            filterType === 'critical' ? 'bg-red-100 border-red-400 ring-2 ring-red-400' : 'bg-red-50 border-red-200 hover:bg-red-100'
          }`}
          data-testid="critical-filter"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Critical (&gt;8min)</p>
              <p className="text-3xl font-bold text-red-600">{criticalCount}</p>
            </div>
            <div className={`w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center ${criticalCount > 0 ? 'animate-bounce' : ''}`}>
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Filter</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-type"
            >
              <option value="all">All Active Orders</option>
              <option value="pending">Pending Only</option>
              <option value="critical">Critical Only</option>
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">Restaurant</label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-restaurant"
            >
              <option value="">All Restaurants</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-date-from"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="filter-date-to"
            />
          </div>
          <button
            onClick={() => {
              setFilterType('all');
              setSelectedRestaurant('');
              setDateFrom('');
              setDateTo('');
            }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <LoadingSpinner />
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
          <p className="text-xl font-medium text-gray-600">All Clear!</p>
          <p className="text-gray-400 mt-1">No active orders requiring attention</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Order ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Customer</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Restaurant</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Order Time</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Time Elapsed</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Rider</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((order) => (
                  <tr 
                    key={order.id} 
                    className={`${getRowClass(order)} transition-colors`}
                    data-testid={`order-row-${order.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          order.service === 'fleety' ? 'bg-blue-500' : 'bg-green-500'
                        }`} title={order.service === 'fleety' ? 'Food' : 'Package'}></span>
                        <span className="font-semibold text-gray-900">#{order.order_number}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{order.customer_name}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[150px]" title={order.customer_address}>
                        {order.customer_address}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{order.restaurant_name}</p>
                      {order.restaurant_phone && (
                        <a 
                          href={`tel:${order.restaurant_phone}`}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" /> {order.restaurant_phone}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatISTTime(order.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold ${
                        order.alert_level === 'critical' 
                          ? 'bg-red-100 text-red-700 animate-pulse' 
                          : order.alert_level === 'warning'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Clock className="w-4 h-4" />
                        {formatElapsed(order.elapsed_seconds)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100'}`}>
                        {order.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {order.rider_name ? (
                        <div>
                          <p className="font-medium text-gray-800 flex items-center gap-1">
                            <Bike className="w-4 h-4 text-green-500" />
                            {order.rider_name}
                          </p>
                          {order.rider_phone && (
                            <a 
                              href={`tel:${order.rider_phone}`}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Phone className="w-3 h-3" /> {order.rider_phone}
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-orange-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {order.rider_phone && (
                          <a
                            href={`tel:${order.rider_phone}`}
                            className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                            title="Call Rider"
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                        {order.restaurant_phone && (
                          <a
                            href={`tel:${order.restaurant_phone}`}
                            className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                            title="Call Restaurant"
                          >
                            <Store className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => setReassignModal(order)}
                          className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition"
                          title="Reassign Order"
                          data-testid={`reassign-btn-${order.id}`}
                        >
                          <Bike className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setCancelModal(order)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                          title="Cancel Order"
                          data-testid={`cancel-btn-${order.id}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reassign Modal */}
      {reassignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReassignModal(null)}>
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Bike className="w-5 h-5 text-purple-600" />
              Reassign Order #{reassignModal.order_number}
            </h3>
            
            {availableRiders.length === 0 ? (
              <div className="text-center py-6">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-600">No online riders available</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableRiders.map((rider) => (
                  <button
                    key={rider.id}
                    onClick={() => setSelectedRider(rider)}
                    className={`w-full p-3 rounded-lg border text-left transition ${
                      selectedRider?.id === rider.id 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{rider.name}</p>
                    <p className="text-sm text-gray-500">{rider.phone}</p>
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setReassignModal(null)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReassign}
                disabled={!selectedRider || actionLoading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {actionLoading ? 'Assigning...' : 'Confirm Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCancelModal(null)}>
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Cancel Order #{cancelModal.order_number}
            </h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action cannot be undone. The customer will need to place a new order.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Cancellation Reason *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-24 resize-none"
                required
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCancelModal(null);
                  setCancelReason("");
                }}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Go Back
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sidebar Navigation
const Sidebar = ({ currentPage, setCurrentPage, onLogout, isOpen, setIsOpen, notificationCount, pendingDeletionsCount, pendingRidersCount }) => {
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "notifications", label: "Notifications", icon: Bell, badge: notificationCount },
    { id: "platform-control", label: "Platform Control", icon: Settings },
    { id: "support-tickets", label: "Support Tickets", icon: Ticket },
    { id: "order-monitoring", label: "Order Monitoring", icon: Radio },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "disputes", label: "Disputes", icon: AlertTriangle },
    { id: "refunds", label: "Refunds", icon: CreditCard },
    { id: "settlements", label: "Settlements", icon: Building },
    { id: "deductions", label: "Deductions", icon: DollarSign },
    { id: "profit-loss", label: "Profit & Loss", icon: FileText },
    { id: "ratings", label: "Ratings", icon: Star },
    { id: "rider-approvals", label: "Rider Approvals", icon: UserCheck, badge: pendingRidersCount, badgeColor: 'bg-yellow-500' },
    { id: "riders", label: "Riders", icon: Bike },
    { id: "customers", label: "Customers", icon: Users },
    { id: "restaurants", label: "Restaurants", icon: Store },
    { id: "registration-requests", label: "Registration Requests", icon: ClipboardList },
    { id: "deletion-requests", label: "Deletion Requests", icon: UserX, badge: pendingDeletionsCount, badgeColor: 'bg-red-500' },
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3FA9F5] rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Fleety Admin</h1>
                <p className="text-xs text-gray-500">Dashboard</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setCurrentPage(item.id); setIsOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${currentPage === item.id ? "bg-[#3FA9F5] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.badge > 0 && (
                  <span className={`ml-auto ${item.badgeColor || 'bg-red-500'} text-white text-xs px-2 py-0.5 rounded-full`}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Main Dashboard Layout
const DashboardLayout = ({ token, onLogout }) => {
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingDeletionsCount, setPendingDeletionsCount] = useState(0);
  const [pendingRidersCount, setPendingRidersCount] = useState(0);
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [fleetyGoPaused, setFleetyGoPaused] = useState(false);
  const api = createApi(token);

  // Fetch notification count on load
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/admin/notifications/all?status=unread');
        setNotificationCount(res.data.unread_count || 0);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        // Fallback to old endpoint
        try {
          const fallback = await api.get('/admin/notifications');
          setNotificationCount(fallback.data.unread_count || 0);
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      }
    };
    
    const fetchPendingDeletions = async () => {
      try {
        const res = await api.get('/admin/deletion-requests/stats');
        setPendingDeletionsCount(res.data.pending_count || 0);
      } catch (err) {
        console.error('Error fetching pending deletions:', err);
      }
    };
    
    const fetchPendingRiders = async () => {
      try {
        const res = await api.get('/admin/riders/pending');
        setPendingRidersCount(res.data.stats?.pending || 0);
      } catch (err) {
        console.error('Error fetching pending riders:', err);
      }
    };
    
    const fetchOrderStatus = async () => {
      try {
        // Fetch Fleety (Food) status
        const fleetyRes = await api.get('/order-status');
        setOrdersPaused(!fleetyRes.data.orders_accepted);
        
        // Fetch FleetyGo (Package) status
        const fleetyGoRes = await api.get('/fleetygo-status');
        setFleetyGoPaused(!fleetyGoRes.data.orders_accepted);
      } catch (err) {
        console.error('Error fetching order status:', err);
      }
    };
    
    fetchNotifications();
    fetchPendingDeletions();
    fetchPendingRiders();
    fetchOrderStatus();
    // Refresh counts every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchPendingDeletions();
      fetchOrderStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [api]);

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard": return <Dashboard api={api} />;
      case "notifications": return <Notifications api={api} onApproval={() => setNotificationCount(prev => Math.max(0, prev - 1))} />;
      case "platform-control": return <PlatformControl api={api} />;
      case "support-tickets": return <SupportTickets api={api} />;
      case "order-monitoring": return <OrderMonitoring api={api} />;
      case "orders": return <Orders api={api} />;
      case "disputes": return <Disputes api={api} />;
      case "refunds": return <Refunds api={api} />;
      case "settlements": return <Settlements api={api} />;
      case "deductions": return <Deductions api={api} />;
      case "profit-loss": return <ProfitLoss api={api} />;
      case "ratings": return <Ratings api={api} />;
      case "rider-approvals": return <RiderApprovals api={api} onApprovalChange={() => { setPendingRidersCount(prev => Math.max(0, prev - 1)); }} />;
      case "riders": return <Riders api={api} token={token} />;
      case "customers": return <Customers api={api} />;
      case "restaurants": return <Restaurants api={api} />;
      case "registration-requests": return <RegistrationRequests api={api} />;
      case "deletion-requests": return <AccountDeletionRequests api={api} />;
      default: return <Dashboard api={api} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="dashboard">
      <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} onLogout={onLogout} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} notificationCount={notificationCount} pendingDeletionsCount={pendingDeletionsCount} pendingRidersCount={pendingRidersCount} />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Fleety (Food) Pause Warning Banner */}
        {ordersPaused && (
          <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-medium">🍔 Fleety Food Orders PAUSED</span>
              <span className="text-orange-100">- Customers cannot place food orders</span>
            </div>
            <button 
              onClick={() => setCurrentPage('platform-control')}
              className="px-3 py-1 bg-white text-orange-600 rounded text-sm font-medium hover:bg-orange-50"
            >
              Manage
            </button>
          </div>
        )}
        {/* FleetyGo (Package) Pause Warning Banner */}
        {fleetyGoPaused && (
          <div className="bg-purple-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              <span className="font-medium">📦 FleetyGo Package Deliveries PAUSED</span>
              <span className="text-purple-100">- Customers cannot request package deliveries</span>
            </div>
            <button 
              onClick={() => setCurrentPage('platform-control')}
              className="px-3 py-1 bg-white text-purple-600 rounded text-sm font-medium hover:bg-purple-50"
            >
              Manage
            </button>
          </div>
        )}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between lg:justify-end">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100" data-testid="mobile-menu-button">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            {/* Notification Bell */}
            <div className="relative">
              <button 
                onClick={() => setCurrentPage('rider-approvals')}
                className="relative p-2 hover:bg-gray-100 rounded-lg"
                title={`${notificationCount + pendingRidersCount} pending items`}
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {(notificationCount + pendingRidersCount) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {notificationCount + pendingRidersCount}
                  </span>
                )}
              </button>
            </div>
            <span className="text-sm text-gray-500">admin@fleety.com</span>
            <div className="w-8 h-8 bg-[#3FA9F5] rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">A</span>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">{renderPage()}</main>
      </div>
    </div>
  );
};

// App Component
function App() {
  const [token, setToken] = useState(localStorage.getItem("fleety_admin_token"));

  const handleLogin = (newToken) => setToken(newToken);
  const handleLogout = () => { localStorage.removeItem("fleety_admin_token"); setToken(null); };

  useEffect(() => {
    if (token) {
      const api = createApi(token);
      api.get("/auth/verify").catch(() => handleLogout());
    }
  }, [token]);

  if (!token) return <LoginPage onLogin={handleLogin} />;
  return <DashboardLayout token={token} onLogout={handleLogout} />;
}

export default App;
